import _ from "lodash";
import React from "react";
import update from "immutability-helper";
import client from "lib/apolloClient/client";
import {
  generateRandomId,
  getYearDurationFormat,
  getDurationFromDates,
  getArchivedSortedSubjectListMemoized,
  getAttachmentUrlFromFieldValue,
  getMemoizedLangFunction,
} from "Utils";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import {
  DifferentiationDuotone,
  LearnerProfileDuotone,
  LearningStandardsDuotone,
  AssessmentsDuotone,
  ObjectivesDuotone,
  OpenBookDuotone,
  AtlDuotone,
  SummativeAssessmentDuotone,
  ThemeDescriptorInsightsDuotone,
  TimelineDuotone,
  SubjectGenericDuotone,
  AtLsAcrossSubjectsDuotone,
  KeyConceptDuotone,
  HorizontalViewAcrossSubjectAreasDuotone,
  SubjectOverviewDuotone,
  ProgrammeofInquiryDuotone,
  GlobalContextDuotone,
  KeyRelatedConceptsDuotone,
  TickLargeOutlined,
  CloseOutlined,
  LeAndTeachingStrategiesDuotone,
} from "@toddle-edu/ds-icons";
import {
  getDynamicPlannerElementLabelStatus,
  getPlannerElementLabelMemoized,
  getRethumb,
  getSubjectGradeId,
  getSubjectGradePairLabel,
  getCPPlannerElementTypesMemoized,
  getCustomPlannerElementSetsQueryVariables,
  getIsCustomDatasetInsightsEnabled,
  sortFlatNodes,
  getTemplateIdsQueryVariables,
} from "PlanningInsights/modules/Utils";
import { colors, fontStyle } from "Constants";
import {
  goToRelativeRoute,
  downloadFromUrl,
  setProgress,
  removeProgressElement,
} from "modules/Services";
import {
  getElementValue,
  checkEmptyElementValue,
} from "modules/ElementValueUtils";
import {
  getPlanningInsightDownloadUrlQuery,
  getOrganizationPlanningInsightDownloadUrlQuery,
  getOverallInsightsDownloadQuery,
  getInsightsIncludedUnitCountQuery,
} from "./Queries";
import {
  updateUnitPlanInclusionInGradeMutation,
  updateUnitPlanInclusionStatusMutation,
} from "./Mutations";
import {
  ATTACHMENT_TYPE_EXCEL,
  CURRICULUM_TYPE_DP,
  CURRICULUM_TYPE_MYP,
  CURRICULUM_TYPE_PYP,
  CURRICULUM_TYPE_UBD,
  MYP_INTERDISCIPLINARY,
  NODE_TYPE_IB_DP_CURRICULUM_PROGRAM,
  NODE_TYPE_IB_MYP_CURRICULUM_PROGRAM,
  NODE_TYPE_IB_PYP_CURRICULUM_PROGRAM,
  NODE_TYPE_UBD_CURRICULUM_PROGRAM,
  SUBJECT_TYPE_GENERIC,
  SUBJECT_TYPE_OTHERS,
  SUBJECT_TYPE_TOK,
  CP_CORE_TYPES,
  ELEMENT_TYPE_UBD_ATL,
  PLANNER_ELEMENT_MYP_GLOBAL_CONTEXT,
  ELEMENT_TYPE_MYP_KEY_CONCEPT,
  ELEMENT_TYPE_MYP_ATL,
  ELEMENT_TYPE_MYP_LEARNER_PROFILE,
  SUBJECT_TYPE_LD,
  SUBJECT_TYPE_PPS,
} from "Constants/stringConstants";
import ACLStore from "lib/aclStore";
import moment from "moment";
import { getDefaultUnitPlanImage } from "UnitPlans/modules/Utils";
import {
  ENABLE_PYP_DYNAMIC_PLANNER_ELEMENT_LABEL,
  ENABLE_MYP_DYNAMIC_PLANNER_ELEMENT_LABEL,
} from "Constants/permissionConstants";
import {
  getCustomPlannerElementSetsFromCache,
  getOrganizationTemplateIdsFromCache,
} from "./GraphqlHelpers";
import {
  getSelectedCurriculumProgram,
  getCurrentAcademicYear,
} from "modules/CommonSelectors";
import { store } from "store/createStore";
import { getUpdatedFieldTypeConfigMemoized } from "UnitPlanDetails/routes/IBPlanner/modules/Utils";
import { isSinglePageEnabled } from "UnitPlans/modules/UnitViewModeUtils";
import { getUserInfo } from "Login/modules/LoginModule";

export const NAME = "planningInsights";
export const UPDATE_FILTERS_OBJECT = "UPDATE_FILTERS_OBJECT" + " " + NAME;
export const CHANGE_CURRENT_TAB = "CHANGE_CURRENT_TAB" + " " + NAME;
export const CHANGE_CURRENT_LEVEL = "CHANGE_CURRENT_LEVEL" + " " + NAME;
export const UPDATE_HORIZONTAL_OVERVIEW_FILTERS =
  "UPDATE_HORIZONTAL_OVERVIEW_FILTERS" + " " + NAME;
export const UPDATE_VERTICAL_OVERVIEW_FILTERS =
  "UPDATE_VERTICAL_OVERVIEW_FILTERS" + " " + NAME;
export const UPDATE_CURRICULUM_MAPS_FILTERS =
  "UPDATE_CURRICULUM_MAPS_FILTERS" + " " + NAME;

export const UPDATE_CURRENT_GRADE = `UPDATE_CURRENT_GRADE ${NAME}`;
export const UPDATE_ADMIN_INSIGHTS_SIDEBAR_SELECTED_ITEM = `UPDATE_ADMIN_INSIGHTS_SIDEBAR_SELECTED_ITEM ${NAME}`;
export const UPDATE_CONFIGURE_UNITS_FILTERS_OBJECT = `UPDATE_CONFIGURE_UNITS_FILTERS_OBJECT ${NAME}`;
export const UPDATE_EXCLUDE_UNIT_MODAL = `UPDATE_EXCLUDE_UNIT_MODAL ${NAME}`;
export const UPDATE_INSIGHT_UNIT_COUNTS = `UPDATE_INSIGHT_UNIT_COUNTS ${NAME}`;
export const UPDATE_CURRENT_CURRICULUM_PROGRAM = `UPDATE_CURRENT_CURRICULUM_PROGRAM ${NAME}`;

const iconSize = 40;
const ALLOWED_TYPES_VERTICAL_OVERVIEW = [
  PLANNER_ELEMENT_MYP_GLOBAL_CONTEXT,
  ELEMENT_TYPE_MYP_ATL,
  ELEMENT_TYPE_MYP_KEY_CONCEPT,
  ELEMENT_TYPE_MYP_LEARNER_PROFILE,
];

const ALLOWED_TYPES_HORIZONTAL_OVERVIEW = [
  ELEMENT_TYPE_MYP_ATL,
  ELEMENT_TYPE_MYP_KEY_CONCEPT,
];

export const INITIAL_CURRICULUM_MAP_FILTERS = {
  unitTypeFilter: "ALL",
  columnOptions: [],
  selectedGrades: [],
  subjectUnitTypeFilter: "ALL",
  curriculumProgramId: null,
};

export const changeCurrentTab = data => {
  return {
    type: CHANGE_CURRENT_TAB,
    data,
  };
};

export const changeCurrentLevel = data => {
  return {
    type: CHANGE_CURRENT_LEVEL,
    data,
  };
};

export const updateCurrentTab = data => {
  return (dispatch, getState) => {
    if (data != getState().planningInsights.currentTab) {
      dispatch(changeCurrentTab(data));
      dispatch(goToRelativeRoute({ route: `../${data}`, type: "replace" }));
    }
  };
};

const _getIsCurrentTabEquiValentToAdmin = state => {
  return _.includes(
    ["accountSettingsTab", "schoolSettingsTab"],
    state.login?.activeTab
  );
};

export const getIsAdminTabActive = state => {
  /**
   * What's going on here?
   *
   * If current tab is admin or accountSettingsTab or schoolSettingsTab, we show admin view of insights.
   * If user is on teacher home, we show admin view of insights.
   */
  return (
    state?.login?.activeTab == "admin" ||
    _getIsCurrentTabEquiValentToAdmin(state) ||
    _.isEmpty(state?.teacher?.selected_class?.selected_course)
  );
};

const getIsWorksheetsEnabled = () =>
  ACLStore.can("FeatureFlag:ClassroomWorksheet");

const getCurrentCurriculumOverviewList = props => {
  const {
    currentCurriculumProgram,
    grades,
    courseSubjectList,
    selectedAcademicYear: { startDate, endDate },
    isAdminTabActive,
    subjectName,
    subjectTags,
    t,
    adminInsightsSelectedSidebarItem = null,
    plannerElementSets,
  } = props;

  const currentCurriculumType = currentCurriculumProgram?.type;

  let currentCurriculumOverview = _.filter(
    getCurriculumOverviewWithCustomDatasets({
      currentCurriculumProgram,
    }),
    item => (item.perm ? ACLStore.can(item.perm) : true)
  );

  const isUbDAdminInsights =
    _.isEqual(currentCurriculumType, CURRICULUM_TYPE_UBD) && isAdminTabActive;
  const isMYPAdminInsights =
    _.isEqual(currentCurriculumType, CURRICULUM_TYPE_MYP) && isAdminTabActive;
  const isPYPAdminInsights =
    _.isEqual(currentCurriculumType, CURRICULUM_TYPE_PYP) && isAdminTabActive;
  const isDPAdminInsights =
    _.isEqual(currentCurriculumType, CURRICULUM_TYPE_DP) && isAdminTabActive;

  if (!isAdminTabActive) {
    // dont show curriculum maps if there's no subjects mapped
    if (_.isEmpty(courseSubjectList)) {
      currentCurriculumOverview = _.filter(
        currentCurriculumOverview,
        overview =>
          !_.includes(["CURRICULUM_MAPS", "SUBJECT_OVERVIEWS"], overview.type)
      );
    }

    // overall programme insights is only available at admin portal
    // and if showing a top header on insight home screen, remove the header from overview list
    currentCurriculumOverview = _.filter(
      currentCurriculumOverview,
      overview => !_.includes(["OVERALL_INSIGHTS", "HEADER"], overview.type)
    );
  }

  if (isUbDAdminInsights || isMYPAdminInsights) {
    // for UbD and MYP - we can select the type of overview from sidebar
    const adminInsightsType = _.get(adminInsightsSelectedSidebarItem, "type");
    const includedTypes = [adminInsightsType];

    // for UbD we show insights header, but not in case of overall insights
    if (isUbDAdminInsights && !_.isEqual(adminInsightsType, "OVERALL_INSIGHTS"))
      includedTypes.push("HEADER");

    currentCurriculumOverview = _.filter(
      currentCurriculumOverview,
      overview => {
        return _.includes(includedTypes, overview.type);
      }
    );
  }

  if (
    _.isEqual(currentCurriculumType, CURRICULUM_TYPE_PYP) &&
    !isPYPAdminInsights
  ) {
    const subjectOverview = _.find(currentCurriculumOverview, {
      type: "SUBJECT_OVERVIEWS",
    });

    currentCurriculumOverview = _.filter(
      currentCurriculumOverview,
      overview => {
        return !_.includes(
          ["HEADER", "SUBJECT_VIEWS", "SUBJECT_OVERVIEWS"],
          overview.type
        );
      }
    );

    // place SUBJECT_OVERVIEWS at end
    if (!_.isEmpty(subjectOverview)) {
      currentCurriculumOverview.push(subjectOverview);
    }
  }

  const currentSubject = adminInsightsSelectedSidebarItem;
  const subjectGroup = isMYPAdminInsights
    ? adminInsightsSelectedSidebarItem
    : props?.subjectGroup;

  if (
    _.isEqual(currentCurriculumType, CURRICULUM_TYPE_DP) &&
    isAdminTabActive
  ) {
    if (
      _.isEqual(adminInsightsSelectedSidebarItem?.id, "horizontal_overview")
    ) {
      currentCurriculumOverview = _.filter(
        currentCurriculumOverview,
        overview => _.isEqual(overview.type, "HORIZONTAL_OVERVIEW")
      );
    } else {
      currentCurriculumOverview = _.filter(
        currentCurriculumOverview,
        overview => !_.isEqual(overview.type, "HORIZONTAL_OVERVIEW")
      );
    }
  }

  return _.map(currentCurriculumOverview, overview => {
    let children = _.filter(overview.children, child => {
      if (_.has(child, "perm") && !ACLStore.can(child.perm)) return false;
      return true;
    });

    if (
      _.includes(
        ["CURRICULUM_MAPS", "SUBJECT_OVERVIEWS", "SUBJECT_VIEWS"],
        overview.type
      )
    ) {
      if (isAdminTabActive && _.isEqual(overview.type, "CURRICULUM_MAPS")) {
        children = _.map(_.get(overview, "children", []), overviewItem => {
          const label = getDynamicPlannerElementLabelStatus({
            item: overviewItem,
          })
            ? `${
                plannerElementSets[overviewItem.elementTypes]?.label
              } ${_.toLower(t("common:view"))}`
            : t(overviewItem.label);
          return {
            value: overviewItem.value,
            icon: overviewItem.icon,
            color: overviewItem.color,
            label,
            type: overviewItem.type,
            route: overviewItem.route,
            shouldGetRethumbURL: false,
          };
        });
      } else {
        const gradeList = isAdminTabActive
          ? [adminInsightsSelectedSidebarItem?.id]
          : _.map(grades, "id");
        // for grade wise subject groups, we only display subjects that are mapped to grades of class
        const gradeSubjectList = _.filter(courseSubjectList, subject =>
          _.some(subject?.grades, grade => _.includes(gradeList, grade?.id))
        );

        children = _.map(
          getArchivedSortedSubjectListMemoized({
            subjectList: _.isEqual(overview.type, "SUBJECT_VIEWS")
              ? gradeSubjectList
              : courseSubjectList,
          }),
          subjectItem => {
            return {
              value: subjectItem.id,
              icon: subjectItem.icon,
              label: subjectItem.name,
              type: _.isEqual(overview.type, "SUBJECT_VIEWS")
                ? "common:subject_view"
                : "planningInsights:type_subject_overview_label",
              route: "units",
              shouldGetRethumbURL: true,
              isArchived: _.get(subjectItem, "isArchived", false),
            };
          }
        );
      }
    } else {
      // some element types have customised labels
      children = _.map(children, child => {
        if (getDynamicPlannerElementLabelStatus({ item: child })) {
          return {
            ...child,
            label: plannerElementSets[child.elementTypes]?.label,
          };
        }
        return child;
      });
    }

    children = _.map(children, child => {
      let route = child.route;
      let label = t(child.label);
      // for DP, we are using the same components for both vertical and horizontal views, no need to separate routes
      if (!_.isEqual(currentCurriculumType, CURRICULUM_TYPE_DP)) {
        switch (overview.type) {
          case "VERTICAL_OVERVIEW":
            route = `verticalOverview/${subjectGroup.id}/${child.route}`;
            break;
          case "HORIZONTAL_OVERVIEW": {
            let grade = grades[0];
            if (
              isUbDAdminInsights ||
              isMYPAdminInsights ||
              isPYPAdminInsights
            ) {
              grade = {
                id: _.get(adminInsightsSelectedSidebarItem, "id", ""),
                name: _.get(adminInsightsSelectedSidebarItem, "label", ""),
              };
            }

            route = `horizontalOverview/${_.get(grade, "id")}/${child.route}`;
            break;
          }
          case "CURRICULUM_MAPS":
            route = isUbDAdminInsights
              ? `curriculumMaps/${adminInsightsSelectedSidebarItem.id}/${child.route}`
              : `curriculumMaps/${child.value}/${child.route}`;

            // can not localize subject name
            label = child.label;
            break;
          case "SUBJECT_OVERVIEWS":
            route = `subjectOverview/${child.value}/${child.route}`;

            // can not localize subject name
            label = child.label;
            break;
          case "SUBJECT_VIEWS":
            route = `subjectView/${child.value}/`;

            if (isAdminTabActive) {
              const gradeId = _.get(adminInsightsSelectedSidebarItem, "id", "");
              route = `${route}?grade=${gradeId}`;
            }

            // can not localize subject name
            label = child.label;
            break;
          case "OVERALL_INSIGHTS":
            route = `overallProgrammeInsights/${child.route}`;
            break;
          case "PROGRAMME_OVERVIEW":
            route = `programmeOverview/${child.route}`;
            break;
        }
      }

      // can not localize custom dataset label as it is user provided
      if (child.isCustomDataset) {
        label = child.label;
      }

      return {
        ...child,
        label,
        type: t(child.type),
        route,
      };
    });
    const isSubjectArchived =
      (overview.type === "HEADER" && isDPAdminInsights) ||
      (overview.type === "CURRICULUM_MAPS" && isUbDAdminInsights);

    const updatedLabels = {
      label: t(overview.label),
      subLabel: overview.subLabelLocale
        ? t(overview.subLabelLocale)
        : overview.subLabel,

      ...(isSubjectArchived && { isArchived: false }),
    };
    switch (overview.type) {
      case "OVERALL_INSIGHTS": {
        const curriculumAcronym = _.get(CURRICULUM_CONFIGURATION, [
          currentCurriculumType,
          "acronym",
        ]);
        updatedLabels.subLabel = t(overview.subLabelLocale, {
          curriculum: t(curriculumAcronym),
        });
        break;
      }
      case "VERTICAL_OVERVIEW": {
        let subjectGroupName = _.get(subjectGroup, "name", "");
        if (isMYPAdminInsights) {
          subjectGroupName = _.get(subjectGroup, "title", "");
          updatedLabels.label = `${subjectGroupName} - ${updatedLabels.label}`;
        }
        updatedLabels.subLabel = t(overview.subLabelLocale, {
          label: subjectGroupName,
        });
        break;
      }
      case "HORIZONTAL_OVERVIEW": {
        let subLabelValue = _.includes(
          [CURRICULUM_TYPE_UBD, CURRICULUM_TYPE_MYP, CURRICULUM_TYPE_PYP],
          currentCurriculumType
        )
          ? t("common:join_array_with_and", {
              array: _.map(grades, "name"),
            })
          : _.get(grades, "0.name", "");
        if (currentCurriculumType != CURRICULUM_TYPE_DP && isAdminTabActive) {
          const selectedGrade = _.get(
            adminInsightsSelectedSidebarItem,
            "label",
            ""
          );
          if (!isPYPAdminInsights) {
            updatedLabels.label = `${selectedGrade}${
              isMYPAdminInsights
                ? ` - ${t("planningInsights:horizontal_overview_label")}`
                : ""
            }`;
          }
          subLabelValue = selectedGrade;
        }
        updatedLabels.subLabel = t(overview.subLabelLocale, {
          label: subLabelValue,
        });
        break;
      }
      case "CURRICULUM_MAPS":
        if (isUbDAdminInsights) {
          updatedLabels.label = _.get(
            adminInsightsSelectedSidebarItem,
            "label",
            ""
          );
          updatedLabels.isArchived = _.get(currentSubject, "isArchived", false);
        }
        break;
      case "SUBJECT_VIEWS": {
        const gradeName = isAdminTabActive
          ? _.get(adminInsightsSelectedSidebarItem, "label", "")
          : t("common:join_array_with_and", {
              array: _.map(grades, "name"),
            });
        updatedLabels.subLabel = t(overview.subLabelLocale, {
          gradeName,
        });
        break;
      }
      case "HEADER": {
        switch (currentCurriculumType) {
          case CURRICULUM_TYPE_PYP: {
            updatedLabels.label = _.get(
              adminInsightsSelectedSidebarItem,
              "label",
              ""
            );
            break;
          }
          case CURRICULUM_TYPE_UBD: {
            updatedLabels.subLabel = null;
            if (isUbDAdminInsights) {
              if (_.size(currentCurriculumOverview) > 1) {
                updatedLabels.label = t(
                  _.last(currentCurriculumOverview).label
                );
              }
            } else {
              updatedLabels.subLabel = `${t(
                "common:academic_year"
              )} ${getYearDurationFormat({
                startDate,
                endDate,
              })}`;
            }

            break;
          }
          case CURRICULUM_TYPE_DP: {
            if (isAdminTabActive) {
              updatedLabels.label = currentSubject?.name;
              updatedLabels.isArchived = _.get(
                currentSubject,
                "isArchived",
                false
              );
            } else
              updatedLabels.subLabel = `${subjectName} ${getYearDurationFormat({
                startDate,
                endDate,
              })}`;
            updatedLabels.tags = subjectTags;
            break;
          }
          default: {
            updatedLabels.subLabel = `${t(
              "common:academic_year"
            )} ${getYearDurationFormat({
              startDate,
              endDate,
            })}`;
          }
        }
      }
    }

    return {
      ...overview,
      ...updatedLabels,
      children,
    };
  });
};

export const getCurrentCurriculumOverviewListMemoize = getMemoizedLangFunction(
  getCurrentCurriculumOverviewList
);

export const CURRICULUM_CONFIGURATION = {
  IB_PYP: {
    acronym: "common:PYP",
    entityType: NODE_TYPE_IB_PYP_CURRICULUM_PROGRAM,
  },
  IB_MYP: {
    acronym: "common:MYP",
    hasSubjectGroups: true,
    entityType: NODE_TYPE_IB_MYP_CURRICULUM_PROGRAM,
  },
  UBD: {
    acronym: "common:UBD",
    hasSubjectGroups: false,
    entityType: NODE_TYPE_UBD_CURRICULUM_PROGRAM,
  },
  IB_DP: {
    acronym: "common:DP",
    hasSubjectGroups: true,
    entityType: NODE_TYPE_IB_DP_CURRICULUM_PROGRAM,
  },
};

/**
 * @deprecated use getCurriculumOverviewWithCustomDatasets or getCurriculumPlanningInsights instead
 */
const CURRICULUM_OVERVIEW = {
  IB_PYP: [
    {
      value: "insights-header",
      label: "common:planning_insights",
      type: "HEADER",
      isBulkDownloadEnabled: false,
      children: [],
    },
    {
      value: "programme-overview",
      label: "planningInsights:programme_overview_label",
      subLabelLocale: "planningInsights:programme_of_inquiry_sub_label",
      type: "PROGRAMME_OVERVIEW",
      isBulkDownloadEnabled: true,
      downloadType: "Planning insights",
      level: "PROGRAM_LEVEL",
      levelName: "planningInsights:program_level",
      children: [
        {
          icon: <ProgrammeofInquiryDuotone size={"small"} color={"pink"} />,
          label: "planningInsights:programme_of_inquiry",
          dropdownLabel: "planningInsights:programme_of_inquiry",
          type: "planningInsights:type_overview_label",
          value: "poi",
          color: "decorativeSurfaceFourL",
          route: "poi",
          plannerElementTypes: ["PYP_POI"],
          canBeDownloaded: true,
          fieldUIDs: [
            "image",
            "title",
            "duration",
            "centralIdea",
            "relatedConcepts",
            "concepts",
            "lois",
          ],
          fieldUIDsForExpandedCell: [
            "centralIdea",
            "concepts",
            "relatedConcepts",
            "lois",
          ],
          hasHours: false,
        },
        {
          icon: <AtlDuotone size={"small"} color={"pink"} />,
          label: "planningInsights:atl",
          dropdownLabel: "planningInsights:atl_insights",
          type: "planningInsights:type_insights_label",
          plannerElementTypes: ["PYP_ATL"],
          elementTypes: ["PYP_ATL"],
          canBeDownloaded: true,
          dynamicPlannerElementLabelPerm: ENABLE_PYP_DYNAMIC_PLANNER_ELEMENT_LABEL,
          table: [
            {
              id: "PYP_ATL_GRADES",
              title: "POI:des_grades_label",
              subTitle: "common:insight_count_table_sub_heading",
              type: "gradeInsight",
              showFilter: true,
            },
            {
              id: "PYP_ATL_THEMES",
              title: "POI:des_transdisciplinary_label",
              subTitle: "common:insight_count_table_sub_heading",
              type: "themeInsight",
            },
          ],
          value: "atls",
          color: "decorativeSurfaceFourL",
          route: "atls",
        },
        {
          icon: (
            <ThemeDescriptorInsightsDuotone size={"small"} color={"pink"} />
          ),
          label: "planningInsights:theme_descriptor",
          dropdownLabel: "planningInsights:theme_descriptor_insights",
          type: "planningInsights:theme_descriptor_label",
          plannerElementTypes: ["PYP_FOCUS_AREA"],
          canBeDownloaded: true,
          table: [
            {
              id: "PYP_FOCUS_AREA_GRADES",
              title: "POI:distribution_transdisciplinary_descriptors",
              type: "gradeInsight",
              showFilter: true,
            },
          ],
          value: "themeFocusAreas",
          color: "decorativeSurfaceFourL",
          route: "themeFocusAreas",
        },
        {
          icon: <KeyRelatedConceptsDuotone size={"small"} color={"pink"} />,
          label: "planningInsights:concepts",
          dropdownLabel: "planningInsights:concept_insight",
          type: "planningInsights:type_insights_label",
          plannerElementTypes: ["PYP_KEY_CONCEPT"],
          dynamicPlannerElementLabelPerm: ENABLE_PYP_DYNAMIC_PLANNER_ELEMENT_LABEL,
          elementTypes: ["PYP_KEY_CONCEPT"],
          canBeDownloaded: true,
          table: [
            {
              id: "PYP_CONCEPT_GRADES",
              title: "POI:des_grades_label",
              subTitle: "common:insight_count_table_sub_heading",
              type: "gradeInsight",
              showFilter: true,
            },
            {
              id: "PYP_CONCEPT_THEMES",
              title: "POI:des_transdisciplinary_label",
              subTitle: "common:insight_count_table_sub_heading",
              type: "themeInsight",
            },
          ],
          value: "keyConcepts",
          color: "decorativeSurfaceFourL",
          route: "keyConcepts",
        },
        {
          icon: <LearnerProfileDuotone size={"small"} color={"pink"} />,
          label: "planningInsights:lpa",
          dropdownLabel: "planningInsights:lpa_insights",
          type: "planningInsights:type_insights_label",
          plannerElementTypes: ["PYP_LEARNER_PROFILE"],
          dynamicPlannerElementLabelPerm: ENABLE_PYP_DYNAMIC_PLANNER_ELEMENT_LABEL,
          elementTypes: ["PYP_LEARNER_PROFILE"],
          canBeDownloaded: true,
          table: [
            {
              id: "PYP_LEARNER_PROFILE_GRADES",
              title: "POI:des_grades_label",
              subTitle: "common:insight_count_table_sub_heading",
              type: "gradeInsight",
              showFilter: true,
            },
            {
              id: "PYP_LEARNER_PROFILE_THEMES",
              title: "POI:des_transdisciplinary_label",
              subTitle: "common:insight_count_table_sub_heading",
              type: "themeInsight",
            },
          ],
          value: "learnerProfile",
          color: "decorativeSurfaceFourL",
          route: "learnerProfile",
        },
        {
          icon: <SubjectGenericDuotone size={"small"} color={"pink"} />,
          label: "planningInsights:subjects",
          dropdownLabel: "planningInsights:subject_insights",
          type: "planningInsights:type_insights_label",
          plannerElementTypes: ["PYP_SUBJECT"],
          canBeDownloaded: true,
          table: [
            {
              id: "PYP_SUBJECT_GRADES",
              title: "POI:des_grades_label",
              subTitle: "planningInsights:subject_insights_table_subtitle",
              type: "gradeInsight",
              showFilter: true,
            },
            {
              id: "PYP_SUBJECT_THEMES",
              title: "POI:des_transdisciplinary_label",
              subTitle: "planningInsights:subject_insights_table_subtitle",
              type: "themeInsight",
            },
          ],
          value: "subjects",
          color: "decorativeSurfaceFourL",
          route: "subjects",
        },
      ],
    },
    {
      value: "subject-overviews",
      label: "planningInsights:subject_overviews",
      subLabelLocale: "planningInsights:subject_overview_sub_label",
      type: "SUBJECT_OVERVIEWS",
      level: "PROGRAM_LEVEL",
      levelName: "planningInsights:program_level",
      isBulkDownloadEnabled: true,
      children: [
        {
          label: "common:unit_view",
          value: "units",
          type: "planningInsights:type_insights_label",
          fieldUIDs: [
            "centralIdea",
            "focusAreas",
            "lois",
            "concepts",
            "relatedConcepts",
            "lp",
            "atls",
            "benchmarks",
            "subjects",
            "theme",
          ],
          downloadFieldUIDs: [],
          elementTypes: [],
          table: [],
          shouldGetRethumbURL: true,
          route: "units",
          canBeDownloaded: true,
          plannerElementTypes: ["PYP_UNIT_VIEW"],
          icon: <ThemeDescriptorInsightsDuotone color="violet" />,
          color: "violet950",
        },
        {
          label: "common:standards_view",
          value: "standards",
          type: "planningInsights:type_insights_label",
          fieldUIDs: [],
          elementTypes: ["PYP_LEARNING_STANDARD"],
          dynamicPlannerElementLabelPerm: ENABLE_PYP_DYNAMIC_PLANNER_ELEMENT_LABEL,
          table: [],
          shouldGetRethumbURL: true,
          route: "standards",
          icon: <LearningStandardsDuotone color="violet" />,
          canBeDownloaded: true,
          plannerElementTypes: ["PYP_LEARNING_STANDARD"],
          color: "violet950",
        },
        {
          label: "unitPlan:timeline_view",
          value: "subject-timeline",
          type: "planningInsights:type_overview_label",
          fieldUIDs: [],
          elementTypes: [],
          table: [],
          shouldGetRethumbURL: true,
          route: "subject-timeline",
          icon: <TimelineDuotone color="violet" />,
          color: "violet950",
        },
      ],
    },
    {
      value: "horizontal-overview",
      label: "planningInsights:horizontal_overview_label",
      subLabelLocale:
        "planningInsights:horizontal_overview_timeline_sub_label_with_grade_name",
      type: "HORIZONTAL_OVERVIEW",
      isBulkDownloadEnabled: false,
      level: "GRADE_LEVEL",
      levelName: "planningInsights:grade_level",
      children: [
        {
          icon: <TimelineDuotone size={"small"} color={"yellow"} />,
          label: "planningInsights:horizontal_overview_timeline_view_label",
          dropdownLabel: "planningInsights:timeline_view",
          type: "planningInsights:type_overview_label",
          value: "timelineView",
          color: "decorativeSurfaceThreeL",
          route: "timeline",
          hasFiltersInHeader: false,
        },
      ],
    },
    {
      value: "subject-views",
      label: "planningInsights:subject_views_label",
      subLabelLocale: "planningInsights:subject_views_sublabel",
      type: "SUBJECT_VIEWS",
      isBulkDownloadEnabled: true,
      level: "GRADE_LEVEL",
      levelName: "planningInsights:grade_level",
      children: [
        // only here for downloads, card is not displayed anywhere
        {
          value: "standards",
          label: "common:learning_standards",
          fieldUIDs: [],
          elementTypes: ["PYP_LEARNING_STANDARD"],
          dynamicPlannerElementLabelPerm: ENABLE_PYP_DYNAMIC_PLANNER_ELEMENT_LABEL,
          canBeDownloaded: true,
          plannerElementTypes: ["PYP_LEARNING_STANDARD"],
        },
      ],
    },
  ],
  IB_MYP: [
    {
      value: "insights-header",
      label: "teacherHomePage:planning_insights",
      subLabelLocale:
        "planningInsights:see_subject_specific_insights_and_coverage_in_units",
      type: "HEADER",
      isBulkDownloadEnabled: false,
      downloadType: "Planning insights",
      isHeader: true,
      children: [],
    },
    {
      value: "vertical-overview",
      label: "planningInsights:vertical_overview_label",
      subLabelLocale:
        "planningInsights:vertical_overview_sub_label_with_subject_group_name",
      type: "VERTICAL_OVERVIEW",
      isBulkDownloadEnabled: true,
      children: [
        {
          icon: <SubjectOverviewDuotone size={"small"} color={"pink"} />,
          label:
            "planningInsights:vertical_overview_subject_group_overview_label",
          dropdownLabel:
            "planningInsights:vertical_overview_subject_group_overview_dropdown_label",
          title: "planningInsights:subject_group_across_subjects",
          value: "sgo",
          downloadFieldUIDs: [
            "title",
            "conceptsMYP",
            "relatedConceptsMYP",
            "globalContextMYP",
            "soi",
            "objectivesMYP",
            "atlsMYP",
            "content",
          ],
          elementTypes: [],
          table: [],
          type: "planningInsights:type_overview_label",
          canBeDownloaded: true,
          plannerElementTypes: ["MYP_SGO"],
          color: "decorativeSurfaceFourL",
          route: "sgo",
          subjectFilterType: "checkList",
          tableListPadding: 32,
          tableListFromTop: 180,
        },
        {
          icon: <ProgrammeofInquiryDuotone size={"small"} color={"pink"} />,
          label:
            "planningInsights:vertical_overview_programme_of_inquiry_label",
          dropdownLabel:
            "planningInsights:vertical_overview_programme_of_inquiry_dropdown_label",
          value: "poi",
          fieldUIDs: [
            "soi",
            "title",
            "image",
            "duration",
            "conceptsMYP",
            "relatedConceptsMYP",
            "hours",
          ],
          fieldUIDsForExpandedCell: [
            "soi",
            "conceptsMYP",
            "relatedConceptsMYP",
          ],
          elementTypes: ["MYP_GLOBAL_CONTEXT"],
          table: [
            {
              id: "MYP_GLOBAL_CONTEXT",
              title: "planningInsights:vertical_overview_poi_table_title",
            },
          ],
          type: "planningInsights:type_overview_label",
          canBeDownloaded: true,
          plannerElementTypes: ["MYP_POI"],
          color: "decorativeSurfaceFourL",
          route: "poi",
          subjectFilterType: "checkList",
          smallerHeaderFontSize: true,
        },
        {
          icon: <GlobalContextDuotone size={"small"} color={"pink"} />,
          label:
            "planningInsights:vertical_overview_global_contexts_insights_label",
          dropdownLabel:
            "planningInsights:vertical_overview_global_contexts_insights_dropdown_label",
          value: "gc",
          fieldUIDs: [],
          elementTypes: ["MYP_GLOBAL_CONTEXT"],
          dynamicPlannerElementLabelPerm: ENABLE_MYP_DYNAMIC_PLANNER_ELEMENT_LABEL,
          table: [
            {
              id: "MYP_GLOBAL_CONTEXT",
              title:
                "planningInsights:vertical_overview_global_context_table_title",
              subTitle: "common:insight_count_table_sub_heading",
            },
          ],
          type: "planningInsights:type_insights_label",
          canBeDownloaded: true,
          plannerElementTypes: ["MYP_GLOBAL_CONTEXT"],
          color: "decorativeSurfaceFourL",
          route: "globalContexts",
          subjectFilterType: "checkList",
        },
        {
          icon: <KeyRelatedConceptsDuotone size={"small"} color={"pink"} />,
          label:
            "planningInsights:vertical_overview_key_and_related_concepts_insights_label",
          dropdownLabel:
            "planningInsights:vertical_overview_key_and_related_concepts_insights_dropdown_label",
          value: "krc",
          fieldUIDs: [],
          elementTypes: ["MYP_KEY_CONCEPT", "MYP_RELATED_CONCEPT"],
          canToggle: false,
          table: [
            {
              id: "MYP_KEY_CONCEPT",
              title:
                "planningInsights:vertical_overview_key_concept_table_title",
              label: "common:key_concepts",
              subjectFilterType: "checkList",
              subTitle: "common:insight_count_table_sub_heading",
              canBeDownloaded: true,
              plannerElementTypes: ["MYP_KEY_CONCEPT"],
            },
            {
              id: "MYP_RELATED_CONCEPT",
              title:
                "planningInsights:vertical_overview_related_concept_table_title",
              label: "common:related_concepts",
              subjectFilterType: "radio",
              subTitle: "common:insight_count_table_sub_heading",
              canBeDownloaded: true,
              plannerElementTypes: ["MYP_RELATED_CONCEPT"],
            },
          ],
          type: "planningInsights:type_insights_label",
          canBeDownloaded: false,
          color: "decorativeSurfaceFourL",
          route: "krc",
          subjectFilterType: "checkList",
        },
        {
          icon: <AtlDuotone size={"small"} color={"pink"} />,
          label:
            "planningInsights:vertical_overview_approaches_to_learning_label",
          dropdownLabel:
            "planningInsights:vertical_overview_approaches_to_learning_dropdown_label",
          value: "atl",
          fieldUIDs: [],
          elementTypes: ["MYP_ATL"],
          dynamicPlannerElementLabelPerm: ENABLE_MYP_DYNAMIC_PLANNER_ELEMENT_LABEL,
          table: [
            {
              id: "MYP_ATL",
              title: "planningInsights:vertical_overview_atl_table_title",
              label: "planningInsights:atl_plural",
              subTitle: "common:insight_count_table_sub_heading",
            },
          ],

          type: "planningInsights:type_insights_label",
          canBeDownloaded: true,
          plannerElementTypes: ["MYP_ATL"],
          color: "decorativeSurfaceFourL",
          route: "skills",
          subjectFilterType: "checkList",
        },
        {
          icon: <ObjectivesDuotone size={"small"} color={"pink"} />,
          label:
            "planningInsights:vertical_overview_MYP_objectives_insights_label",
          dropdownLabel:
            "planningInsights:vertical_overview_MYP_objectives_insights_dropdown_label",
          value: "objectives",
          fieldUIDs: [],
          elementTypes: ["MYP_OBJECTIVES"],
          dynamicPlannerElementLabelPerm: ENABLE_MYP_DYNAMIC_PLANNER_ELEMENT_LABEL,
          canToggle: false,
          table: [
            {
              id: "MYP_OBJECTIVES",
              title:
                "planningInsights:vertical_overview_objectives_table_title",
              subTitle: "common:insight_count_table_sub_heading",
            },
          ],
          type: "planningInsights:type_insights_label",
          canBeDownloaded: true,
          plannerElementTypes: ["MYP_OBJECTIVES"],
          color: "decorativeSurfaceFourL",
          route: "objectives",
          subjectFilterType: "radio",
        },
        {
          icon: <SubjectGenericDuotone size={"small"} color={"pink"} />,
          label: "common:subject_standards",
          dropdownLabel: "common:subject_standards",
          type: "planningInsights:type_insights_label",
          value: "standards",
          table: [
            {
              id: "MYP_LEARNING_STANDARD",
              title: "planningInsights:vertical_overview_standards_table_title",
              subTitle: "common:standard_tables_subtext",
              dynamicSubtitle: "common:task_tables_subtext_with_label",
            },
          ],
          elementTypes: ["MYP_LEARNING_STANDARD"],
          plannerElementTypes: ["MYP_LEARNING_STANDARD"],
          dynamicPlannerElementLabelPerm: ENABLE_MYP_DYNAMIC_PLANNER_ELEMENT_LABEL,
          canBeDownloaded: true,
          color: "decorativeSurfaceFourL",
          route: "standards",
          tableListPadding: 60,
          tableListFromTop: 160,
        },
        {
          icon: <LearnerProfileDuotone size={"small"} color={"pink"} />,
          subLabel: "learner profile attribute",
          label: "planningInsights:lpa",
          dropdownLabel: "planningInsights:lpa",
          type: "planningInsights:type_insights_label",
          value: "lpas",
          elementTypes: ["MYP_LEARNER_PROFILE"],
          dynamicPlannerElementLabelPerm: ENABLE_MYP_DYNAMIC_PLANNER_ELEMENT_LABEL,
          table: [
            {
              id: "MYP_LEARNER_PROFILE",
              title: "planningInsights:vertical_overview_lpas_table_title",
              subTitle: "common:insight_count_table_sub_heading",
            },
          ],
          color: "decorativeSurfaceFourL",
          route: "lpas",
          plannerElementTypes: ["MYP_LEARNER_PROFILE"],
          canBeDownloaded: true,
        },
        {
          icon: <TimelineDuotone size={"small"} color={"pink"} />,
          label: "planningInsights:subject_group_timeline_view",
          dropdownLabel: "planningInsights:subject_group_timeline_view",
          type: "planningInsights:type_overview_label",
          value: "sgo-timeline",
          table: [],
          elementTypes: [],
          color: "decorativeSurfaceFourL",
          route: "sgo-timeline",
          title: "planningInsights:timeline_view_across_all_year_levels",
          subjectFilterType: "dropdown",
        },
      ],
    },
    {
      value: "horizontal-overview",
      label: "planningInsights:horizontal_overview_label",
      subLabelLocale:
        "planningInsights:horizontal_overview_sub_label_with_grade_name",
      type: "HORIZONTAL_OVERVIEW",
      isBulkDownloadEnabled: true,
      children: [
        {
          icon: <TimelineDuotone size={"small"} color={"yellow"} />,
          label: "planningInsights:horizontal_overview_timeline_view_label",
          dropdownLabel: "planningInsights:timeline_view",
          type: "planningInsights:type_overview_label",
          value: "timelineView",
          color: "decorativeSurfaceThreeL",
          route: "timeline",
          hasFiltersInHeader: false,
        },
        {
          icon: (
            <HorizontalViewAcrossSubjectAreasDuotone
              size={"small"}
              color={"yellow"}
            />
          ),
          label:
            "planningInsights:horizontal_overview_organised_by_global_contexts_label",
          dropdownLabel:
            "planningInsights:horizontal_overview_organised_by_global_contexts_dropdown_label",
          value: "gc",
          fieldUIDs: [
            "soi",
            "title",
            "image",
            "relatedConceptsMYP",
            "conceptsMYP",
            "duration",
            "hours",
          ],
          fieldUIDsForExpandedCell: [
            "soi",
            "relatedConceptsMYP",
            "conceptsMYP",
          ],
          elementTypes: ["MYP_GLOBAL_CONTEXT"],
          dynamicPlannerElementLabelPerm: ENABLE_MYP_DYNAMIC_PLANNER_ELEMENT_LABEL,
          table: [
            {
              id: "MYP_GLOBAL_CONTEXT",
              title:
                "planningInsights:horizontal_overview_global_context_table_title",
              subTitle:
                "planningInsights:horizontal_overview_global_context_table_subTitle",
              dynamicSubtitle:
                "planningInsights:horizontal_overview_table_subtitle_with_label",
            },
          ],
          type: "planningInsights:type_overview_label",
          canBeDownloaded: true,
          plannerElementTypes: ["MYP_GLOBAL_CONTEXT"],
          color: "decorativeSurfaceThreeL",
          route: "globalContexts",
          hasFiltersInHeader: true,
        },
        {
          icon: <KeyConceptDuotone size={"small"} color={"yellow"} />,
          label:
            "planningInsights:horizontal_overview_organised_by_key_concepts_label",
          dropdownLabel:
            "planningInsights:horizontal_overview_organised_by_key_concepts_dropdown_label",
          value: "kc",
          fieldUIDs: [
            "soi",
            "title",
            "image",
            "subjectGroup",
            "duration",
            "hours",
          ],
          fieldUIDsForExpandedCell: ["soi", "subjectGroup"],
          elementTypes: ["MYP_KEY_CONCEPT"],
          dynamicPlannerElementLabelPerm: ENABLE_MYP_DYNAMIC_PLANNER_ELEMENT_LABEL,
          table: [
            {
              id: "MYP_KEY_CONCEPT",
              title:
                "planningInsights:horizontal_overview_key_concept_table_title",
              subTitle:
                "planningInsights:horizontal_overview_key_concept_table_subtitle",
              dynamicSubtitle:
                "planningInsights:horizontal_overview_table_subtitle_with_label",
            },
          ],
          type: "planningInsights:type_overview_label",
          canBeDownloaded: true,
          plannerElementTypes: ["MYP_KEY_CONCEPT"],
          color: "decorativeSurfaceThreeL",
          route: "keyConcepts",
          hasFiltersInHeader: true,
        },
        {
          icon: <AtLsAcrossSubjectsDuotone size={"small"} color={"yellow"} />,
          label:
            "planningInsights:horizontal_overview_approaches_to_learning_label",
          dropdownLabel:
            "planningInsights:horizontal_overview_approaches_to_learning_dropdown_label",
          value: "atl",
          fieldUIDs: [],
          elementTypes: ["MYP_ATL"],
          dynamicPlannerElementLabelPerm: ENABLE_MYP_DYNAMIC_PLANNER_ELEMENT_LABEL,
          table: [
            {
              id: "MYP_ATL",
              title: "planningInsights:horizontal_overview_atl_table_title",
              label: "planningInsights:atl_plural",
              subTitle: "common:insight_count_table_sub_heading",
              dynamicSubtitle: "common:insight_count_table_sub_heading",
            },
          ],
          type: "planningInsights:type_insights_label",
          canBeDownloaded: true,
          plannerElementTypes: ["MYP_ATL"],
          color: "decorativeSurfaceThreeL",
          route: "skills",
          hasFiltersInHeader: true,
        },
      ],
    },
    {
      value: "overall-insights",
      label: "planningInsights:overall_programme_insights_label",
      subLabelLocale: "planningInsights:overall_programme_insights_sublabel",
      type: "OVERALL_INSIGHTS",
      isBulkDownloadEnabled: true,
      children: [
        {
          icon: <AtlDuotone size={"small"} color={"pink"} />,
          label:
            "planningInsights:horizontal_overview_approaches_to_learning_label",
          dropdownLabel:
            "planningInsights:horizontal_overview_approaches_to_learning_label",
          value: "atl",
          plannerElementTypes: ["MYP_ATL"],
          elementTypes: ["MYP_ATL"],
          dynamicPlannerElementLabelPerm: ENABLE_MYP_DYNAMIC_PLANNER_ELEMENT_LABEL,
          table: [
            {
              id: "MYP_ATL",
              label: "planningInsights:atl_plural",
              title:
                "planningInsights:overall_programme_insights_table_title_with_label",
              subTitle: "common:insight_count_table_sub_heading",
            },
          ],
          type: "planningInsights:type_insights_label",
          canBeDownloaded: true,
          color: "decorativeSurfaceFourL",
          route: "skills",
        },
        {
          icon: <GlobalContextDuotone size={"small"} color={"pink"} />,
          label:
            "planningInsights:vertical_overview_global_contexts_insights_label",
          dropdownLabel:
            "planningInsights:vertical_overview_global_contexts_insights_label",
          value: "gc",
          plannerElementTypes: ["MYP_GLOBAL_CONTEXT"],
          elementTypes: ["MYP_GLOBAL_CONTEXT"],
          dynamicPlannerElementLabelPerm: ENABLE_MYP_DYNAMIC_PLANNER_ELEMENT_LABEL,
          table: [
            {
              id: "MYP_GLOBAL_CONTEXT",
              title:
                "planningInsights:overall_programme_insights_table_title_with_label",
              subTitle: "common:insight_count_table_sub_heading",
            },
          ],
          type: "planningInsights:type_insights_label",
          canBeDownloaded: true,
          color: "decorativeSurfaceFourL",
          route: "globalContexts",
        },
        {
          icon: <LearnerProfileDuotone size={"small"} color={"pink"} />,
          label: "planningInsights:lpa",
          dropdownLabel: "planningInsights:lpa",
          value: "lpas",
          plannerElementTypes: ["MYP_LEARNER_PROFILE"],
          elementTypes: ["MYP_LEARNER_PROFILE"],
          dynamicPlannerElementLabelPerm: ENABLE_MYP_DYNAMIC_PLANNER_ELEMENT_LABEL,
          table: [
            {
              id: "MYP_LEARNER_PROFILE",
              title:
                "planningInsights:overall_programme_insights_table_title_with_label",
              subTitle: "common:insight_count_table_sub_heading",
            },
          ],
          type: "planningInsights:type_insights_label",
          canBeDownloaded: true,
          color: "decorativeSurfaceFourL",
          route: "lpas",
        },
      ],
    },
  ],
  UBD: [
    {
      value: "insights-header",
      label: "teacherHomePage:planning_insights",
      subLabelLocale:
        "planningInsights:see_subject_specific_insights_and_coverage_in_units",
      type: "HEADER",
      isBulkDownloadEnabled: false,
      downloadType: "Planning insights",
      isHeader: true,
      children: [],
    },
    {
      value: "curriculum-maps",
      label: "common:curriculum_maps",
      subLabel:
        "Units and standards coverage across all grades for a specific subject",
      type: "CURRICULUM_MAPS",
      isBulkDownloadEnabled: true,
      children: [
        {
          label: "common:unit_view",
          value: "units",
          type: "planningInsights:type_insights_label",
          fieldUIDs: [
            "about",
            "learningStandardUBD",
            "atlsUBD",
            "transferGoals",
            "studentUnderstand",
            "teacherQues",
            "subjects",
            "duration",
          ],
          downloadFieldUIDs: [
            "title",
            "about",
            "learningStandardUBD",
            "atlsUBD",
            "transferGoals",
            "studentUnderstand",
            "teacherQues",
          ],
          elementTypes: [],
          table: [],
          shouldGetRethumbURL: true,
          route: "units",
          canBeDownloaded: true,
          plannerElementTypes: ["UBD_UNIT_VIEW"],
          icon: <ThemeDescriptorInsightsDuotone color="violet" />,
          color: "violet950",
        },
        {
          label: "common:standards_view",
          value: "standards",
          type: "planningInsights:type_insights_label",
          fieldUIDs: [],
          elementTypes: ["UBD_LEARNING_STANDARD"],
          isDynamicPlannerElementLabel: true,
          table: [],
          shouldGetRethumbURL: true,
          route: "standards",
          icon: <LearningStandardsDuotone color="violet" />,
          canBeDownloaded: true,
          plannerElementTypes: ["UBD_LEARNING_STANDARD"],
          color: "violet950",
        },
        {
          label: "unitPlan:timeline_view",
          value: "subject-timeline",
          type: "planningInsights:type_overview_label",
          fieldUIDs: [],
          elementTypes: [],
          table: [],
          shouldGetRethumbURL: true,
          route: "subject-timeline",
          icon: <TimelineDuotone color="violet" />,
          color: "violet950",
        },
      ],
    },
    {
      value: "horizontal-overview",
      label: "planningInsights:horizontal_overview_label",
      subLabelLocale: "common:ubd_horizontal_overview_sub_label",
      type: "HORIZONTAL_OVERVIEW",
      isBulkDownloadEnabled: true,
      isAdminBulkDownloadEnabled: false,
      children: [
        {
          id: 20,
          icon: (
            <TimelineDuotone
              height={iconSize}
              width={iconSize}
              color={"pink"}
            />
          ),
          label: "common:units_timeline_view",
          type: "planningInsights:type_overview_label",
          value: "timeline",
          elementTypes: [],
          fieldUIDs: [],
          table: [],
          canBeDownloaded: false,
          color: "surfaceError",
          route: "timeline",
          hasFiltersInHeader: false,
        },
        {
          id: 21,
          icon: (
            <AtlDuotone height={iconSize} width={iconSize} color={"pink"} />
          ),
          label: "common:skill_plural",
          isDynamicPlannerElementLabel: true,
          type: "planningInsights:type_insights_label",
          value: "skills",
          elementTypes: ["UBD_ATL"],
          fieldUIDs: [],
          table: [],
          canBeDownloaded: true,
          color: "surfaceError",
          route: "skills",
          perm: "TeacherPortal:UBD_Atls",
          hasFiltersInHeader: true,
          plannerElementTypes: ["UBD_ATL"],
        },
      ],
    },
    {
      value: "overall-insights",
      label: "planningInsights:overall_programme_insights_label",
      subLabelLocale: "planningInsights:overall_programme_insights_sublabel",
      type: "OVERALL_INSIGHTS",
      isBulkDownloadEnabled: true,
      children: [
        {
          icon: <AtlDuotone size={"small"} color={"pink"} />,
          label:
            "planningInsights:horizontal_overview_approaches_to_learning_label",
          dropdownLabel:
            "planningInsights:horizontal_overview_approaches_to_learning_label",
          value: "atl",
          elementTypes: ["UBD_ATL"],
          plannerElementTypes: ["UBD_ATL"],
          table: [
            {
              id: "UBD_ATL",
              title:
                "planningInsights:overall_programme_insights_table_title_with_label",
              subTitle: "common:insight_count_table_sub_heading",
            },
          ],
          isDynamicPlannerElementLabel: true,
          type: "planningInsights:type_insights_label",
          canBeDownloaded: true,
          color: "decorativeSurfaceFourL",
          route: "skills",
        },
      ],
    },
  ],
  IB_DP: [
    {
      value: "insights-header",
      label: "teacherHomePage:planning_insights",
      subLabelLocale:
        "planningInsights:see_subject_specific_insights_and_coverage_in_units",
      type: "HEADER",
      isBulkDownloadEnabled: false,
      downloadType: "Planning",
      isHeader: true,
    },
    {
      value: "subject-overview",
      label: "planningInsights:subject_overview_label",
      type: "VERTICAL_OVERVIEW",
      isBulkDownloadEnabled: true,
      children: [
        {
          icon: <OpenBookDuotone size={"small"} color={"pink"} />,
          label: "common:syllabus_content_label",
          dropdownLabel: "common:syllabus_content_label",
          type: "planningInsights:type_insights_label",
          value: "syllabusContent",
          plannerElementTypes: ["DP_SYLLABUS"],
          color: "decorativeSurfaceFourL",
          route: "syllabusContent",
          skipForSubjectGroup: ["CORE", "OTHERS"],
          canBeDownloaded: true,
        },
        {
          icon: <SummativeAssessmentDuotone size={"small"} color={"pink"} />,
          label: "unitPlan:syllabus_outline",
          dropdownLabel: "unitPlan:syllabus_outline",
          type: "planningInsights:type_insights_label",
          value: "syllabusOutline",
          plannerElementTypes: ["DP_KNOWLEDGE_FRAMEWORK"],
          skipForSubjectGroup: ["GENERIC", "OTHERS", "CP_CORE"],
          color: "decorativeSurfaceFourL",
          route: "syllabusOutline",
          canBeDownloaded: true,
        },
        {
          icon: <ObjectivesDuotone size={"small"} color={"pink"} />,
          subLabel: "aim",
          canToggle: false,
          label: "common:aim_label",
          dropdownLabel: "common:aim_label",
          type: "planningInsights:type_insights_label",
          value: "aims",
          plannerElementTypes: ["DP_AIM"],
          color: "decorativeSurfaceFourL",
          skipForSubjectGroup: ["OTHERS"],
          route: "aims",
          canBeDownloaded: true,
        },
        {
          icon: <AssessmentsDuotone size={"small"} color={"pink"} />,
          subLabel: "assessment objective",
          canToggle: false,
          label: "common:objectives_label",
          dropdownLabel: "common:objectives_label",
          type: "planningInsights:type_insights_label",
          value: "assessmentObjectives",
          plannerElementTypes: ["DP_OBJECTIVES"],
          color: "decorativeSurfaceFourL",
          skipForSubjectGroup: ["OTHERS", "CP_CORE"],
          route: "assessmentObjectives",
          canBeDownloaded: true,
        },
        {
          icon: <DifferentiationDuotone size={"small"} color={"pink"} />,
          label: "planningInsights:connections",
          dropdownLabel: "planningInsights:connections",
          type: "planningInsights:type_insights_label",
          value: "connections",
          plannerElementTypes: [
            "DP_ATL",
            "DP_METACOGNITION_APPROACH",
            "DP_DIFFERENTIATION_APPROACH",
            "DP_LANGUAGE_AND_LEARNING_CONNECTION",
            "DP_TOK_CONNECTION",
            "DP_CAS_STRANDS",
          ],
          color: "decorativeSurfaceFourL",
          route: "connections",
          canBeDownloaded: true,
        },
        {
          icon: <LearningStandardsDuotone size={"small"} color={"pink"} />,
          subLabel: "standard",
          canToggle: true,
          label: "common:subject_standards",
          dropdownLabel: "common:subject_standards",
          type: "planningInsights:type_insights_label",
          value: "standards",
          plannerElementTypes: ["DP_SUBJECT_STANDARD"],
          skipForSubjectGroup: CP_CORE_TYPES,
          color: "decorativeSurfaceFourL",
          route: "standards",
          canBeDownloaded: true,
        },
        {
          icon: <LearnerProfileDuotone size={"small"} color={"pink"} />,
          subLabel: "learner profile attribute",
          label: "planningInsights:lpa",
          dropdownLabel: "planningInsights:lpa",
          type: "planningInsights:type_insights_label",
          value: "lpas",
          plannerElementTypes: ["DP_LEARNER_PROFILE"],
          color: "decorativeSurfaceFourL",
          route: "lpas",
          canBeDownloaded: true,
        },
        {
          icon: (
            <LeAndTeachingStrategiesDuotone size={"small"} color={"pink"} />
          ),
          label: "common:benchmarks_label",
          dropdownLabel: "common:benchmarks_label",
          type: "planningInsights:type_insights_label",
          value: "learning-outcomes",
          plannerElementTypes: ["DP_CP_PPS_LEARNING_OUTCOMES"],
          color: "decorativeSurfaceFourL",
          route: "learning-outcomes",
          canBeDownloaded: true,
          skipForSubjectGroup: ["CORE", "OTHERS", "GENERIC", "TOK"],
          skipForSubjectType: ["LD", "SLR", "RP", "CE"],
        },
      ],
    },
    {
      value: "horizontal-overview",
      label: "planningInsights:horizontal_overview_label",
      type: "HORIZONTAL_OVERVIEW",
      isBulkDownloadEnabled: true,
      children: [
        {
          icon: (
            <TimelineDuotone
              height={iconSize}
              width={iconSize}
              color={"violet"}
            />
          ),
          label: "common:units_timeline_view",
          dropdownLabel: "common:units_timeline_view",
          title: "planningInsights:dp_unit_timeline_view_header",
          type: "planningInsights:type_overview_label",
          value: "timeline",
          plannerElementTypes: [],
          color: "violet950",
          route: "timeline",
        },
        {
          icon: (
            <AtlDuotone height={iconSize} width={iconSize} color={"violet"} />
          ),
          label: "planningInsights:connections",
          dropdownLabel: "planningInsights:connections",
          title: "planningInsights:connections_horizontal_view_table_header",
          type: "planningInsights:type_insights_label",
          plannerElementTypes: [
            "DP_ATL",
            "DP_METACOGNITION_APPROACH",
            "DP_LANGUAGE_AND_LEARNING_CONNECTION",
            "DP_TOK_CONNECTION",
            "DP_CAS_STRANDS",
          ],
          color: "violet950",
          value: "horizontal-connections",
          route: "horizontal-connections",
          canBeDownloaded: true,
        },
        {
          icon: (
            <LearnerProfileDuotone
              height={iconSize}
              width={iconSize}
              color={"violet"}
            />
          ),
          label: "planningInsights:lpa",
          subLabel: "learner profile attribute",
          dropdownLabel: "planningInsights:lpa",
          title: "planningInsights:lpas_horizontal_view_table_header",
          type: "planningInsights:type_insights_label",
          plannerElementTypes: ["DP_LEARNER_PROFILE"],
          color: "violet950",
          value: "horizontal-lpas",
          route: "horizontal-lpas",
          canBeDownloaded: true,
        },
      ],
    },
  ],
};

const PLANNING_INSIGHTS_WITH_CUSTOM_DATASET_SUPPORT = [
  "programme-overview",
  "vertical-overview",
  "subject-overview",
  "horizontal-overview",
  "overall-insights",
];

const HORIZONTAL_OVERVIEW_COLOR_MAP = {
  IB_PYP: { backgroundColor: "decorativeSurfaceThreeL", iconColor: "yellow" },
  IB_MYP: { backgroundColor: "decorativeSurfaceThreeL", iconColor: "yellow" },
  IB_DP: { backgroundColor: "violet950", iconColor: "violet" },
  UBD: { backgroundColor: "decorativeSurfaceFourL", iconColor: "pink" },
};

const MYP_BETA_ELEMENT_TYPES = [
  "MYP_SPECIFIED_CONCEPTS",
  "MYP_COMMUNITY_ENGAGEMENT",
  "MYP_LEARNING_OBJECTIVES",
];

const MYP_BETA_TEMPLATE_IDS = [
  "myp_interdisciplinary_beta",
  "myp_standalone_beta",
];

export const getCurriculumOverviewWithCustomDatasets = ({
  currentCurriculumProgram: _currentCurriculumProgram,
  curriculumProgramType: _curriculumProgramType,
}) => {
  const state = store?.getState();

  let currentCurriculumProgram = _currentCurriculumProgram;
  const userInfo = getUserInfo();
  const organizationId = userInfo.org_id;

  // in case of SnP, we might not receive the current curriculum program
  if (_.isEmpty(currentCurriculumProgram)) {
    currentCurriculumProgram = _.get(
      state,
      "planningInsights.currentCurriculumProgram",
      {}
    );
  }

  // check if planning insights of a specific curriculum were requested
  // if not, use the current selected curriculum type
  const curriculumProgramType =
    _curriculumProgramType ?? currentCurriculumProgram?.type;
  const curriculumProgramId = currentCurriculumProgram?.id;

  let updatedCurriculumPlanningInsights = _.get(
    CURRICULUM_OVERVIEW,
    curriculumProgramType,
    []
  );

  const templateQueryVariables = getTemplateIdsQueryVariables({
    organizationId,
    curriculumProgramId,
  });

  const templates = _.get(
    getOrganizationTemplateIdsFromCache(templateQueryVariables),
    "templates",
    []
  );

  // add insights for MYP beta elements if they are present in the curriculum
  const hasBetaTemplates =
    curriculumProgramType === CURRICULUM_TYPE_MYP &&
    _.some(templates, template => {
      const { id } = template;
      const baseTemplateId = _.get(template, "baseTemplate.id", null);

      return (
        _.includes(MYP_BETA_TEMPLATE_IDS, id) ||
        _.includes(MYP_BETA_TEMPLATE_IDS, baseTemplateId)
      );
    });

  if (hasBetaTemplates) {
    const plannerElementSets = _.get(
      state,
      "platform.currentPlannerElementSets",
      []
    );

    const betaPlannerInsights = _.map(MYP_BETA_ELEMENT_TYPES, type => {
      const plannerElementSet = _.get(plannerElementSets, type, {}) ?? {};
      const { id: plannerElementSetId, label } = plannerElementSet;

      const isNested = type === "MYP_SPECIFIED_CONCEPTS";

      return {
        label,
        dropdownLabel: label,
        type: "planningInsights:type_insights_label",
        plannerElementSets: [type],
        plannerElementSetId,
        elementTypes: [type],
        plannerElementTypes: [type],
        canBeDownloaded: false,
        value: type,
        color: "decorativeSurfaceFourL",
        route: type,
        canToggle: isNested,
        hasFiltersInHeader: true,
        isBetaElement: true,
      };
    });

    updatedCurriculumPlanningInsights = _.map(
      updatedCurriculumPlanningInsights,
      insight => {
        const isHorizontalOverview = insight.value === "horizontal-overview";
        const isHeader = insight.type === "HEADER";

        // no updates need to be made in header since only text appears there
        if (isHeader) {
          return insight;
        }

        let _betaPlannerInsights = betaPlannerInsights;

        if (isHorizontalOverview) {
          const colorMap = _.get(
            HORIZONTAL_OVERVIEW_COLOR_MAP,
            curriculumProgramType
          );

          _betaPlannerInsights = _.map(_betaPlannerInsights, insight => {
            return {
              ...insight,
              color: colorMap.backgroundColor,
              iconColor: colorMap.iconColor,
            };
          });
        }

        return {
          ...insight,
          children: [...insight.children, ..._betaPlannerInsights],
        };
      }
    );
  }

  if (!getIsCustomDatasetInsightsEnabled()) {
    return updatedCurriculumPlanningInsights;
  }

  const customDatasetQueryVariables = getCustomPlannerElementSetsQueryVariables(
    { currentCurriculumProgram }
  );

  const customDatasets = _.get(
    getCustomPlannerElementSetsFromCache(customDatasetQueryVariables),
    "plannerElementSets",
    []
  );

  const isCurriculumPYP = curriculumProgramType === CURRICULUM_TYPE_PYP;

  const customDatasetInsights = _.map(customDatasets, dataset => {
    const { id, label, type } = dataset;

    const isNested = type === "NESTED_CHECKLIST";

    const insight = {
      isCustomDataset: true,
      // can not return icon from here since this is passed into multiple
      // memoized functions and react components can not be stringified
      // icon: <InsightsSquareOutlined size={"small"} color={"pink"} />,
      label,
      dropdownLabel: label,
      type: "planningInsights:type_insights_label",
      plannerElementSets: [id],
      canBeDownloaded: true,
      value: id,
      color: "decorativeSurfaceFourL",
      route: id,
      canToggle: isNested,
      hasFiltersInHeader: true,
    };

    if (isCurriculumPYP) {
      insight.table = [
        {
          id: `${id}_GRADES`,
          title: label,
          subTitle: "common:insight_count_table_sub_heading",
          type: "gradeInsight",
          showFilter: true,
        },
        {
          id: `${id}_THEMES`,
          title: label,
          subTitle: "common:insight_count_table_sub_heading",
          type: "themeInsight",
        },
      ];
    }

    return insight;
  });

  return _.map(updatedCurriculumPlanningInsights, insight => {
    if (
      _.includes(PLANNING_INSIGHTS_WITH_CUSTOM_DATASET_SUPPORT, insight.value)
    ) {
      const isHorizontalOverview = insight.value === "horizontal-overview";
      let _customDatasetInsights = customDatasetInsights;

      if (isHorizontalOverview) {
        // we only show timeline view in PYP horizontal view
        if (isCurriculumPYP) {
          return insight;
        }

        const colorMap = _.get(
          HORIZONTAL_OVERVIEW_COLOR_MAP,
          curriculumProgramType
        );

        _customDatasetInsights = _.map(_customDatasetInsights, insight => {
          return {
            ...insight,
            color: colorMap.backgroundColor,
            iconColor: colorMap.iconColor,
          };
        });

        if (curriculumProgramType === CURRICULUM_TYPE_DP) {
          // in DP, there is no distinction between vertical and horizontal overview in routes
          // so we need to append the route with -horizontal
          _customDatasetInsights = _.map(_customDatasetInsights, insight => ({
            ...insight,
            route: `${insight.route}-horizontal`,
            value: `${insight.value}-horizontal`,
          }));
        }
      }

      return {
        ...insight,
        children: [...insight.children, ..._customDatasetInsights],
      };
    }
    return insight;
  });
};

// helper dispatch util to get planning insights without passing curriculum type
export const getCurriculumPlanningInsights = (params = {}) => {
  const { curriculumProgramType } = params;

  return (dispatch, getState) => {
    const state = getState();
    const currentCurriculumProgram = getSelectedCurriculumProgram(state);

    return getCurriculumOverviewWithCustomDatasets({
      currentCurriculumProgram,
      curriculumProgramType,
    });
  };
};

export const CURRICULUM_MAPS_TABS = {
  STANDARDS: "standards",
  UNITS: "units",
  TIMELINE: "subject-timeline",
};

export const DOWNLOAD_TYPES = {
  VERTICAL_OVERVIEW: "VERTICAL_VIEW",
  CURRICULUM_MAPS: "VERTICAL_VIEW",
  SUBJECT_OVERVIEWS: "VERTICAL_VIEW",
  SUBJECT_VIEWS: "HORIZONTAL_VIEW",
  HORIZONTAL_OVERVIEW: "HORIZONTAL_VIEW",
};

export const TEMPLATE_FIELD_OBJECTS = {
  subjectGroup: {
    config: {
      viewLabel: "Subject group",
      viewLabelLocale: "planningInsights:subject_group",
    },
    viewType: "TextView",
  },
  soi: {
    config: {
      viewLabel: "Statement of inquiry",
      viewLabelLocale: "planningInsights:soi",
    },
    viewType: "TextView",
  },
  conceptsMYP: {
    config: {
      viewLabel: "Key concepts",
      viewLabelLocale: "planningInsights:key_concepts",
      plannerElementConfig: {
        type: "MYP_KEY_CONCEPT",
      },
      type: "PLANNER_ELEMENT",
    },
    viewType: "TextView",
    dynamicPlannerElementLabelPerm: ENABLE_MYP_DYNAMIC_PLANNER_ELEMENT_LABEL,
  },
  relatedConceptsMYP: {
    config: {
      viewLabel: "Related concepts",
      viewLabelLocale: "planningInsights:related_concepts",
      plannerElementConfig: {
        type: "MYP_RELATED_CONCEPT",
      },
      type: "PLANNER_ELEMENT",
    },
    viewType: "TextView",
    dynamicPlannerElementLabelPerm: ENABLE_MYP_DYNAMIC_PLANNER_ELEMENT_LABEL,
  },
  centralIdea: {
    config: {
      viewLabel: "Central idea",
      viewLabelLocale: "planningInsights:central_idea",
    },
    viewType: "TextView",
  },
  relatedConcepts: {
    config: {
      viewLabel: "Related concepts",
      viewLabelLocale: "planningInsights:related_concepts",
      plannerElementConfig: {
        type: "PYP_RELATED_CONCEPT",
      },
      type: "PYP_PLANNER_ELEMENT",
    },
    viewType: "IBRelatedConcepts",
    dynamicPlannerElementLabelPerm: ENABLE_PYP_DYNAMIC_PLANNER_ELEMENT_LABEL,
  },
  concepts: {
    config: {
      viewLabel: "Key concepts",
      viewLabelLocale: "planningInsights:key_concepts",
      plannerElementConfig: {
        type: "PYP_KEY_CONCEPT",
      },
      type: "PYP_PLANNER_ELEMENT",
    },
    viewType: "TextView",
    dynamicPlannerElementLabelPerm: ENABLE_PYP_DYNAMIC_PLANNER_ELEMENT_LABEL,
  },
  lois: {
    config: {
      viewLabel: "Lines of inquiry",
      viewLabelLocale: "planningInsights:lines_of_inquiry",
    },
    viewType: "ListView",
  },
  lp: {
    config: {
      viewLabel: "Learner profile attributes",
      viewLabelLocale: "planningInsights:lpa",
      plannerElementConfig: {
        type: "PYP_LEARNER_PROFILE",
      },
      type: "PYP_PLANNER_ELEMENT",
    },
    viewType: "TextView",
  },
  atls: {
    config: {
      viewLabel: "Approaches to learning",
      viewLabelLocale: "planningInsights:atl",
      plannerElementConfig: {
        type: "PYP_ATL",
      },
      type: "PYP_PLANNER_ELEMENT",
    },
    viewType: "Nestedview",
  },
};

const DEFAULT_PADDING = 24,
  PADDING_STEP = 16;

const INSIGHT_LABEL_CLASSES = [
  "text-label-2",
  "text-body-1",
  "text-body-2",
  "text-body-2",
];

const getIndentedStyle = ({ depth, hasPadding = true }) => {
  const paddedStyle = {
    dataRowHeaderCellContentStyle: {
      paddingLeft:
        depth >= 4
          ? // padding is increased in steps of 16 per level + the default 24 padding
            DEFAULT_PADDING + PADDING_STEP * (depth - 3)
          : DEFAULT_PADDING,
      // need minor left margin to align bullets properly in non collapsible views
      marginLeft: depth >= 3 ? 2 : 0,
    },
    // although we do not have levels below depth 3
    // but for collapsible views such as ATL and SS - we are increasing depth by 1
    ...(depth >= 3
      ? {
          dataRowHeaderCellInnerStyle: {
            display: "list-item",
            marginLeft: 16,
            listStyleType: depth === 4 ? "circle" : "disc",
            color: depth === 4 ? colors.textSubtle : colors.textDefault,
          },
        }
      : {}),
  };

  return {
    labelClass: _.get(INSIGHT_LABEL_CLASSES, depth - 1, "text-body-1"),
    ...(hasPadding ? paddedStyle : {}),
  };
};

const getInsightTableStyles = ({
  depth = 1,
  elementType,
  isCollapsible = false,
}) => {
  // not all planner elements have the same depth structure
  // need to modify the depth for uniform styling
  let updatedDepth = depth;

  switch (elementType) {
    case "PYP_ATL":
    case "MYP_ATL":
    case "MYP_SPECIFIED_CONCEPTS":
    case "UBD_ATL":
    case "DP_ATL":
    case "MYP_LEARNING_STANDARD":
    case "UBD_LEARNING_STANDARD":
    case "DP_SUBJECT_STANDARD":
    case "PYP_KEY_CONCEPT":
    case "PYP_LEARNER_PROFILE":
    case "PYP_SUBJECT":
    case "CARD":
    case "CHECKLIST":
    case "NESTED_CHECKLIST":
    case "MYP_COMMUNITY_ENGAGEMENT":
    case "MYP_LEARNING_OBJECTIVES":
      updatedDepth = depth + 1;
      break;

    case "DP_LEARNER_PROFILE":
    case "DP_METACOGNITION_APPROACH":
    case "DP_LANGUAGE_AND_LEARNING_CONNECTION":
    case "DP_CAS_STRANDS":
    case "DP_DIFFERENTIATION_APPROACH":
    case "MYP_GLOBAL_CONTEXT":
    case "MYP_LEARNER_PROFILE":
      updatedDepth = depth + 2;
      break;

    case "MYP_KEY_CONCEPT":
      updatedDepth = depth == 0 ? 2 : depth;
      break;

    case "MYP_OBJECTIVES": {
      updatedDepth = depth - 1;
      break;
    }

    case "PYP_LEARNING_STANDARD": {
      // in collapsible view, it looks a bit loud when everything is collapsed due to text-label-1
      // change depth to 1 level down so text is in body-1 instead
      if (isCollapsible) updatedDepth = depth + 1;
      break;
    }
  }

  return getIndentedStyle({ depth: updatedDepth });
};

const smallCellStyle = {
  minWidth: "160px",
};

const mediumCellStyle = {
  minWidth: "280px",
};

const largeCellStyle = {
  minWidth: "360px",
};

const minMediumCellStyle = {
  minWidth: "280px",
};
const minLargeCellStyle = {
  minWidth: "420px",
};

const defaultFieldContentStyle = {
  fontSize: "1.4rem",
  color: colors.neutral200,
  fontWeight: 500,
  lineHeight: "1.9rem",
};

const styles = {
  emptyFieldContentStyle: {
    conceptsMYP: { ...defaultFieldContentStyle, ...smallCellStyle },
    relatedConceptsMYP: { ...defaultFieldContentStyle, ...smallCellStyle },
    globalContextMYP: { ...defaultFieldContentStyle, ...mediumCellStyle },
    soi: { ...defaultFieldContentStyle, ...mediumCellStyle },
    objectivesMYP: { ...defaultFieldContentStyle, ...largeCellStyle },
    atlsMYP: { ...defaultFieldContentStyle, ...largeCellStyle },
    content: { ...defaultFieldContentStyle, ...largeCellStyle },
    about: { ...defaultFieldContentStyle, ...mediumCellStyle },
    learningStandardUBD: { ...defaultFieldContentStyle, ...mediumCellStyle },
    atlsUBD: { ...defaultFieldContentStyle, ...mediumCellStyle },
    transferGoals: { ...defaultFieldContentStyle, ...mediumCellStyle },
    studentUnderstand: { ...defaultFieldContentStyle, ...mediumCellStyle },
    teacherQues: { ...defaultFieldContentStyle, ...mediumCellStyle },
    disciplinaryContent: { ...defaultFieldContentStyle, ...largeCellStyle },
    centralIdea: { ...defaultFieldContentStyle, ...mediumCellStyle },
    lois: { ...defaultFieldContentStyle, ...mediumCellStyle },
    concepts: { ...defaultFieldContentStyle, ...mediumCellStyle },
    relatedConcepts: { ...defaultFieldContentStyle, ...mediumCellStyle },
    lp: { ...defaultFieldContentStyle, ...mediumCellStyle },
    atls: { ...defaultFieldContentStyle, ...mediumCellStyle },
    benchmarks: { ...defaultFieldContentStyle, ...mediumCellStyle },
  },
  unveilContainerStyle: {
    conceptsMYP: mediumCellStyle,
    relatedConceptsMYP: smallCellStyle,
    globalContextMYP: mediumCellStyle,
    soi: mediumCellStyle,
    objectivesMYP: largeCellStyle,
    atlsMYP: largeCellStyle,
    content: largeCellStyle,
    disciplinaryContent: largeCellStyle,
    learningStandardMYP: minLargeCellStyle,
    about: minMediumCellStyle,
    learningStandardUBD: minLargeCellStyle,
    atlsUBD: minLargeCellStyle,
    transferGoals: minMediumCellStyle,
    studentUnderstand: minMediumCellStyle,
    teacherQues: minMediumCellStyle,
    hours: smallCellStyle,
    centralIdea: minMediumCellStyle,
    lois: minLargeCellStyle,
    concepts: minLargeCellStyle,
    relatedConcepts: minLargeCellStyle,
    lp: minLargeCellStyle,
    atls: minLargeCellStyle,
    benchmarks: minLargeCellStyle,
    theme: minMediumCellStyle,
  },
  nodeLabelStyle: {
    objectivesMYP: [
      {
        depth: 1,
        style: {
          fontSize: "1.4rem",
          color: colors.pink100,
        },
      },
    ],
    relatedConceptsMYP: [
      {
        depth: 1,
        style: {
          fontSize: "1.4rem",
        },
      },
    ],
  },
  childrenContainerStyle: [
    { style: { gridRowGap: "12px" }, depth: 0 },
    { style: { gridRowGap: "12px" }, depth: 1 },
    { style: { gridRowGap: "12px", marginTop: "12px" }, depth: 2 },
    { style: { gridRowGap: "12px", marginTop: "12px" }, depth: 3 },
    { style: { gridRowGap: "8px", marginTop: "8px" }, depth: 4 },
    { style: { gridRowGap: "8px", marginTop: "8px" }, depth: 5 },
  ],
  relatedConceptListStyle: {
    mainContainerStyle: { marginBottom: 8 },
    headerContainerStyle: {
      marginBottom: 8,
    },
    headerLabelStyle: {
      ...fontStyle.demiBold,
      fontSize: "1.6rem",
    },
    listStyle: {
      marginBottom: 12,
    },
    listHeaderStyle: {
      ...fontStyle.medium,
      fontSize: "1.4rem",
      color: colors.textDefault,
    },
    listItemsStyle: {
      ...fontStyle.regular,
      fontSize: "1.4rem",
    },
  },
  groupedKeyConceptStyle: {
    conceptLabelStyle: {
      ...fontStyle.medium,
      color: colors.textSubtle,
    },
    headerLabelStyle: {
      ...fontStyle.demiBold,
      lineHeight: "2.4rem",
    },
  },
};

/**
 * Generating Table Data for MYP Overview
 */

const getOverviewHeaderRowData = ({ configuredColumns, t }) => {
  return [
    {
      id: "1",
      value: t("common:unit"),
      style: {},
    },
    ..._.map(configuredColumns, column => {
      const { id, label, isHidden } = column;
      return {
        id,
        value: label,
        style: _.get(styles, ["unveilContainerStyle", id], mediumCellStyle),
        isHidden,
      };
    }),
  ];
};

const INTERDISCIPLINARY_FIELD_MAPPING = {
  learningStandardMYP: "disciplinaryLearningStandardMYP",
  objectivesMYP: "disciplinaryObjectivesMYP",
  relatedConceptsMYP: "disciplinaryRelatedConceptsMYP",
};

const getIsNumber = value => {
  return /^\d+$/.test(value);
};

const getOverviewBodyRowsData = ({
  configuredColumns,
  unitPlanDetailsList,
  t,
  subjects = [],
  insightFields,
  fieldListsV2,
}) => {
  return _.map(unitPlanDetailsList, _unitPlanDetails => {
    const unitPlanDetails = _.get(_unitPlanDetails, "node", _unitPlanDetails);
    const unitPlanFields = _.get(
      unitPlanDetails,
      "allFields",
      _.get(unitPlanDetails, "fields", [])
    );

    const templateId = unitPlanDetails.templateId;
    const imageUrl = getAttachmentUrlFromFieldValue({
      fieldValue: _.get(unitPlanDetails, "image", {}),
    });

    const unitPlanTitle = _.get(unitPlanDetails, "title.value", "");

    const startDate = _.get(unitPlanDetails, "startDate", null);
    const endDate = _.get(unitPlanDetails, "endDate", null);
    const duration = _.get(
      _.find(unitPlanFields, { uid: "duration" }),
      "value",
      null
    );

    const hours = _.get(
      _.find(unitPlanFields, { uid: "hours" }),
      "value",
      null
    );

    const unitPlanSubjects = _.get(
      _.find(unitPlanFields, { uid: "subjects" }),
      "resolvedMinimalTree.subjects",
      null
    );

    const shouldDeriveDurationFromDates =
      moment(startDate).isValid() && moment(endDate).isValid();
    const durationValue = shouldDeriveDurationFromDates
      ? getDurationFromDates({ startDate, endDate })
      : _.get(duration, "value.week", 0);

    const weekString = t("common:weekCount", { count: durationValue });
    const hourString = t("common:hour_count", { count: hours ?? 0 });

    const allFieldsData = _.map(configuredColumns, column => {
      const {
        id: fieldKey,
        isHidden,
        mergedWithField,
        showLabel,
        isYesNoField,
      } = column;
      let fieldUID = fieldKey;

      // check if we should use FieldComponentV2 for it
      const fieldListV2 = _.get(fieldListsV2, templateId, []);
      const isPortedField = _.has(fieldListV2, fieldKey);

      const isUnitTypeMYPInterdiscplinary =
        _.get(_.find(unitPlanFields, { uid: "unitType" }), "value", "") ==
        MYP_INTERDISCIPLINARY;

      // *HACK: This is because, for subject specific fieldUid is content
      // for interdisciplinary it is disciplinaryContent
      // If we change in the insightFields, we will get 2 different columns
      // But we want to show in a single column, so using this
      if (fieldKey == "content" && isUnitTypeMYPInterdiscplinary) {
        fieldUID = "disciplinaryContent";
      }

      if (!isPortedField && !_.has(insightFields, [fieldKey, templateId])) {
        return {
          id: fieldUID,
          value: "N/A",
          isComplete: false,
          style: styles.emptyFieldContentStyle[fieldUID],
          isHidden,
        };
      }
      const fieldObj = _.get(insightFields, [fieldKey, templateId], {});

      const { viewType, subFields, type } = fieldObj;

      let renderType = viewType ? viewType : type;

      // for MYP interdisciplinary units, key concepts are supposed to be displayed
      // in TextView and not grouped by subject groups
      if (
        isUnitTypeMYPInterdiscplinary &&
        _.isEqual(renderType, "GroupedConceptsView")
      )
        renderType = "TextView";

      let unitPlanFieldValue = _.get(
        _.find(unitPlanFields, { uid: fieldUID }),
        "value",
        ""
      );

      let resolvedValue =
        _.get(
          _.find(unitPlanFields, { uid: fieldUID }),
          "resolvedMinimalTree",
          ""
        ) || {};

      // for interdisciplinary fields, we need to show data from all the subjects
      if (
        isUnitTypeMYPInterdiscplinary &&
        _.has(INTERDISCIPLINARY_FIELD_MAPPING, fieldUID)
      ) {
        const interdisciplinaryUid = INTERDISCIPLINARY_FIELD_MAPPING[fieldUID];
        let mergedValue = [],
          mergedNodes = [];

        _.forEach(unitPlanFields, field => {
          // if fieldUid starts with interdisciplinaryUid, then merge the fields
          // but not the interdisciplinaryUid field itself because its value is always empty
          if (
            _.startsWith(field.uid, interdisciplinaryUid) &&
            !_.isEqual(field.uid, interdisciplinaryUid)
          ) {
            mergedValue = _.concat(mergedValue, field.value);
            mergedNodes = _.concat(
              mergedNodes,
              _.get(field, "resolvedMinimalTree.nodes", [])
            );
          }
        });
        unitPlanFieldValue = mergedValue;
        if (resolvedValue)
          resolvedValue = update(resolvedValue, {
            nodes: { $set: mergedNodes },
          });
      }

      const subjectIds = _.get(
        _.find(unitPlanFields, { uid: "subjects" }),
        "value",
        ""
      );

      const subjectGroup = _.first(
        _.get(
          _.find(unitPlanFields, { uid: "subjectGroup" }),
          "resolvedMinimalTree.subjectGroups",
          []
        )
      );

      const updatedSubjects = _.isEmpty(subjects) ? unitPlanSubjects : subjects;

      let value = getElementValue({
        fieldUID,
        valueKeys: unitPlanFieldValue,
        resolvedValue,
        isUnitPlan: true,
        fieldObj,
        subjectIds,
        subjects: updatedSubjects,
        unitPlanFields,
      });

      if (fieldUID === "lois" && isSinglePageEnabled()) {
        value = unitPlanFieldValue;

        if (renderType === "ListView") {
          renderType = "InputNumberList";
        }
      }

      if (fieldUID === "hours") {
        if (getIsNumber(unitPlanFieldValue) && Number(unitPlanFieldValue) > 0) {
          value = unitPlanFieldValue;
        }
      }

      let fieldComponentV2Props = {};

      if (isPortedField) {
        const fieldData = _.find(unitPlanFields, { uid: fieldUID }, {});
        const fieldObjV2 = _.get(fieldListV2, fieldKey, {});
        const uiType = _.get(fieldObjV2, "uiType.value");

        const isReflectionField = uiType === "IBReflection";

        fieldComponentV2Props = {
          isPortedField,
          value: unitPlanFieldValue,
          fieldKey: fieldUID,
          type: `${uiType}_Ported`,
          isReflectionField,
          showReflection: isReflectionField,
          fieldObj: fieldData,
          fieldId: fieldData.id,
        };
      }

      const isComplete =
        !isPortedField && !isYesNoField
          ? checkEmptyElementValue({
              fieldKey: fieldUID,
              data: unitPlanDetails,
              unitPlanFields,
              fieldObj,
              isUnitPlan: true,
              subjects: updatedSubjects,
              resolvedValue,
              unitPlanTitle,
            })
          : true;

      let subHeadingLabel = `${moment(startDate).format("DD MMM")} - ${moment(
        endDate
      ).format("DD MMM")}`;

      if (hours) {
        subHeadingLabel = `${subHeadingLabel} • ${hourString} `;
      }

      const options = {
        subjects: _.get(unitPlanDetails, "subjects.value", []),
        groupedOptionsFields: ["subjects", "concepts"],
        concepts: _.get(
          _.find(unitPlanFields, { uid: "concepts" }),
          "value",
          ""
        ),
      };

      // common props independent of if field is complete or not
      const commonProps = {
        id: fieldUID,
        isComplete,
        mergedWithField,
        isHidden,
      };

      const fieldTypeConfig = _.get(fieldObj, "config.fieldTypeConfig", {});
      const subText = _.get(fieldObj, "config.subText", "");

      const updatedFieldTypeConfig = getUpdatedFieldTypeConfigMemoized({
        fieldTypeConfig,
        hasSubtext: !_.isEmpty(subText),
        mode: "view",
        fieldKey,
        t,
      });

      return isComplete
        ? {
            ...commonProps,
            showLabel,
            value: {
              field: {
                subFields,
                type: renderType,
                mode: "view",
                value,
                fieldUID,
                subjectGroup,
                nodes: resolvedValue.nodes,
                applyDefaultClass: false,
                defaultOptions: options,
                resolvedValue,
                depthOffset: 1,
                isYesNoField,
                fieldTypeConfig: updatedFieldTypeConfig,
                ...fieldComponentV2Props,
              },
              unitPlan: {
                id: unitPlanDetails?.id,
                imageSrc: imageUrl,
                headerLabel: unitPlanTitle,
                subHeadingLabel,
                fieldLabel: _.get(fieldObj, "config.label", null),
              },
            },
            style: {
              nodeLabelStyle: styles.nodeLabelStyle[fieldUID],
              childrenContainerStyle: styles.childrenContainerStyle,
              unveilContainerStyle: _.get(
                styles,
                ["unveilContainerStyle", fieldUID],
                mediumCellStyle
              ),
              groupConceptHeaderStyle: styles.groupConceptHeaderStyle,
              // to avoid conflicts, only spread if component matches view type
              ...(_.isEqual(type, "IBRelatedConcepts")
                ? styles.relatedConceptListStyle
                : {}),
              ...(_.isEqual(viewType, "GroupedConceptsView")
                ? styles.groupedKeyConceptStyle
                : {}),
            },
          }
        : {
            ...commonProps,
            value: _.has(insightFields, [fieldUID, templateId]) ? "-" : "N/A",
            style: styles.emptyFieldContentStyle[fieldUID],
          };
    });

    return [
      {
        id: unitPlanDetails.id,
        label: unitPlanTitle,
        value: getDefaultUnitPlanImage({
          id: unitPlanDetails.id,
          imageUrl: imageUrl,
          getRethumb,
        }),
        subText: hours ? `${hourString} • ${weekString}` : weekString,
      },
      ...allFieldsData,
    ];
  });
};

const generateOverviewTableData = ({
  configuredColumns,
  unitPlanDetailsList,
  t,
  subjects,
  insightFields,
  fieldListsV2,
}) => {
  const table = [];

  const headerRowData = getOverviewHeaderRowData({
    configuredColumns,
    t,
  });

  const bodyRowsData = getOverviewBodyRowsData({
    configuredColumns,
    unitPlanDetailsList,
    insightFields,
    t,
    subjects,
    fieldListsV2,
  });

  return _.concat(table, [headerRowData], bodyRowsData);
};

const mimeTypeMapAttachmentType = {
  EXCEL: "application/vnd.ms-excel",
  ZIP: "application/zip",
};

// anything above this limit results in renaming file to "example.com"
const MAX_SAFARI_FILE_NAME_LENGTH = 130;

const getTruncatedAttachmentName = ({ attachmentName }) => {
  return _.truncate(attachmentName, {
    length: MAX_SAFARI_FILE_NAME_LENGTH,
    omission: "...",
  });
};

export const downloadDPPlanningInsights = ({
  subjectName,
  orgName,
  yearGroupName,
  insightItems,
  insightName,
  yearGroupId,
  attachmentType,
  subjectId,
  selectedAcademicYear: { startDate, endDate },
  isCPCore,
}) => {
  return async dispatch => {
    // format of attachment name shoud be orgname - subjectname yearGroupName InsightName (academic-year) - date
    let attachmentName = `${orgName} - ${subjectName} ${yearGroupName} ${insightName} (${getYearDurationFormat(
      {
        startDate,
        endDate,
      }
    )}) - ${moment().format("D MMMM YYYY")}`;

    attachmentName = getTruncatedAttachmentName({ attachmentName });

    const downloadableInsightItems = _.filter(insightItems, item => {
      return item.canBeDownloaded;
    });

    const files = _.map(downloadableInsightItems, insight => {
      const isCustomDataset = insight.isCustomDataset;

      let sheets = insight.plannerElementTypes;

      if (isCPCore) {
        sheets = getCPPlannerElementTypesMemoized({
          plannerElementTypes: insight.plannerElementTypes,
          insightType: insight.route,
        });
      } else if (isCustomDataset) {
        sheets = insight.plannerElementSets;
      }

      return {
        fileName: insight.label,
        sheets,
      };
    });

    const plannerElementSetIds = _.reduce(
      downloadableInsightItems,
      (result, item) => {
        if (item.isCustomDataset) {
          const _plannerElementSetIds = _.get(item, "plannerElementSets", []);
          result = _.concat(result, ..._plannerElementSetIds);
        }
        return result;
      },
      []
    );

    const fileId = generateRandomId();
    try {
      const attachment = {
        id: fileId,
        type: "FILE",
        name: attachmentName,
        mimeType: mimeTypeMapAttachmentType[attachmentType],
      };
      dispatch(
        setProgress({
          id: fileId,
          progress: 0,
          attachment,
          status: "SAVE",
          type: "FILE",
        })
      );
      const result = await client.query({
        query: getPlanningInsightDownloadUrlQuery,
        variables: {
          id: subjectId,
          unitPlanInsightUrlInput: {
            filters: {
              yearGroupId,
              plannerElementSetIds,
            },
            downloadURLDetails: {
              files,
              folderName: attachmentName,
            },
          },
        },
        fetchPolicy: "network-only",
      });

      const planningInsightDownloadresult = _.get(
        result,
        "data.node.generateUnitPlanInsightUrl",
        {}
      );

      if (planningInsightDownloadresult.isSuccess) {
        if (_.isNull(planningInsightDownloadresult.url)) {
          dispatch(removeProgressElement({ id: fileId }));
          dispatch(
            setToastMsg({
              msg: "toastMsgs:file_does_not_exist",
              type: "warning",
              position: "toast-top-center",
            })
          );
        } else {
          attachment.url = planningInsightDownloadresult.url;
          dispatch(downloadFromUrl({ attachment, shouldAskForSave: true }));
        }
      } else {
        dispatch(removeProgressElement({ id: fileId }));
        dispatch(
          setToastMsg({
            msg: "toastMsgs:something_went_wrong",
            type: "warning",
            position: "toast-top-center",
          })
        );
      }
    } catch (e) {
      dispatch(removeProgressElement({ id: fileId }));

      if (e.response) {
        dispatch(
          setToastMsg({
            msg: "toastMsgs:something_went_wrong",
            type: "warning",
            position: "toast-top-center",
          })
        );
      } else if (e.networkError) {
        dispatch(
          setToastMsg({
            msg: "toastMsgs:no_internet_connection",
            type: "warning",
            position: "toast-top-center",
          })
        );
      }
    }
  };
};

export const downloadPlanningInsights = ({
  t,
  organizationId,
  organizationName,
  insightItems,
  insightName,
  insightTitle,
  filters,
  insightType,
  attachmentType = ATTACHMENT_TYPE_EXCEL,
  selectedAcademicYear: { id: academicYearId, startDate, endDate },
  isOverallInsights = false,
  isLabelLocalized = false,
  isCPCore,
}) => {
  return async (dispatch, getState) => {
    const state = getState();
    const plannerElementSets = _.get(
      state,
      "platform.currentPlannerElementSets",
      {}
    );
    const isAdminTabActive = getIsAdminTabActive(state);

    let attachmentName = `${organizationName} - ${insightTitle} (${getYearDurationFormat(
      {
        startDate,
        endDate,
      }
    )})${insightName ? ` - ${insightName}` : ""} - ${moment().format(
      "D MMMM YYYY"
    )}`;

    attachmentName = getTruncatedAttachmentName({ attachmentName });

    const files = [];
    _.forEach(insightItems, insight => {
      if (insight?.canBeDownloaded) {
        let sheets = _.get(insight, "plannerElementTypes", []);

        if (insight.isCustomDataset) {
          sheets = _.get(insight, "plannerElementSets", []);
        } else {
          if (
            (_.isEqual(insight.value, "horizontal-connections") &&
              isAdminTabActive) ||
            isCPCore
          )
            sheets = getCPPlannerElementTypesMemoized({
              plannerElementTypes: insight.plannerElementTypes,
              insightType: insight.value,
              isAdminTabActive,
            });
        }

        files.push({
          fileName: isLabelLocalized
            ? insight.label
            : getPlannerElementLabelMemoized({
                item: insight,
                plannerElementSets,
              }),
          sheets,
          fieldUids: _.get(insight, "downloadFieldUIDs", []),
        });
        // if insight has multiple tables, individual sheet is downloaded for each table
        // so we keep elementTypes stored separately for each table
      } else if (_.has(insight, "table") && _.size(insight.table) > 1) {
        _.forEach(insight.table, tableItem => {
          if (tableItem?.canBeDownloaded) {
            files.push({
              fileName: t(tableItem.label),
              sheets: _.get(tableItem, "plannerElementTypes", []),
            });
          }
        });
      }
    });
    const fileId = generateRandomId();
    try {
      const attachment = {
        id: fileId,
        type: "FILE",
        name: attachmentName,
        mimeType: mimeTypeMapAttachmentType[attachmentType],
      };
      dispatch(
        setProgress({
          id: fileId,
          progress: 0,
          attachment,
          status: "SAVE",
          type: "FILE",
        })
      );

      let query = getOrganizationPlanningInsightDownloadUrlQuery;

      const variables = {
        id: organizationId,
        input: {
          filters: {
            academicYears: academicYearId ? [academicYearId] : [],
            ...filters,
          },
          downloadURLDetails: {
            files,
            folderName: attachmentName,
          },
        },
      };

      if (isOverallInsights) {
        const curriculumProgramId = state.platform.currentCurriculumProgram.id;
        const curriculumProgramType =
          state.platform.currentCurriculumProgram.type;

        query = getOverallInsightsDownloadQuery;
        variables["id"] = curriculumProgramId;
        variables["curriculumProgramType"] = _.get(CURRICULUM_CONFIGURATION, [
          curriculumProgramType,
          "entityType",
        ]);
      } else {
        variables["input"]["type"] = insightType;
      }

      const result = await client.query({
        query,
        variables,
        fetchPolicy: "network-only",
      });

      const planningInsightDownloadResult = _.get(
        result,
        ["data", "node", "insightsDownloadUrl"],
        {}
      );

      if (_.get(planningInsightDownloadResult, "isSuccess", false)) {
        if (_.isNull(planningInsightDownloadResult.url)) {
          dispatch(removeProgressElement({ id: fileId }));
          dispatch(
            setToastMsg({
              msg: "toastMsgs:file_does_not_exist",
              type: "warning",
              position: "toast-top-center",
            })
          );
        } else {
          attachment.url = planningInsightDownloadResult.url;
          dispatch(downloadFromUrl({ attachment, shouldAskForSave: true }));
        }
      } else {
        dispatch(removeProgressElement({ id: fileId }));
        dispatch(
          setToastMsg({
            msg: "toastMsgs:something_went_wrong",
            type: "warning",
            position: "toast-top-center",
          })
        );
      }
    } catch (e) {
      dispatch(removeProgressElement({ id: fileId }));

      if (e.response) {
        dispatch(
          setToastMsg({
            msg: "toastMsgs:something_went_wrong",
            type: "warning",
            position: "toast-top-center",
          })
        );
      } else if (e.networkError) {
        dispatch(
          setToastMsg({
            msg: "toastMsgs:no_internet_connection",
            type: "warning",
            position: "toast-top-center",
          })
        );
      }
    }
  };
};

const getTableDataWithConfiguredColumns = ({
  t,
  subjectOverviewColumns,
  insightColumns,
  insightFields,
  unitPlanDetailsList,
  fieldListsV2,
}) => {
  // need to retain the order from subjectOverviewColumns
  const configuredColumns = _.reduce(
    subjectOverviewColumns,
    (result, fieldKey) => {
      const insightColumn = _.find(insightColumns, { id: fieldKey });
      if (insightColumn) result.push(insightColumn);
      return result;
    },
    []
  );

  const tableData = generateOverviewTableData({
    unitPlanDetailsList,
    configuredColumns,
    insightFields,
    fieldListsV2,
    t,
  });
  return tableData;
};

export const getUBDPYPOverviewData = ({
  t,
  subjectOverviewData,
  subjectOverviewColumns,
  gradeInsightFieldsMap,
  insightColumns,
  fieldListsV2,
}) => {
  return _.map(subjectOverviewData["grades"], grade => {
    const unitPlanDetailsList = _.get(grade, "unitPlans", []);
    const tableId = grade.id;
    const tableLabel = grade.name;

    const insightFields = _.get(gradeInsightFieldsMap, `${grade.id}`, {});

    const tableData = getTableDataWithConfiguredColumns({
      t,
      subjectOverviewColumns,
      insightColumns,
      insightFields,
      unitPlanDetailsList,
      fieldListsV2,
    });

    const emptyLabel = t("unitPlan:no_units_added");
    return {
      emptyLabel,
      id: tableId,
      value: tableData,
      label: tableLabel,
    };
  });
};

const getOverviewTableUnitPlans = ({
  t,
  subjectOverviewColumns,
  unitPlans,
  insightColumns,
  gradeInsightFieldsMap,
  currentGrade,
  fieldListsV2,
}) => {
  const insightFields = _.get(gradeInsightFieldsMap, `${currentGrade}`, {});
  const tableData = getTableDataWithConfiguredColumns({
    t,
    subjectOverviewColumns,
    insightColumns,
    insightFields,
    unitPlanDetailsList: unitPlans,
    fieldListsV2,
  });

  const emptyLabel = t("unitPlan:no_units_added");
  return [
    {
      emptyLabel,
      id: currentGrade,
      value: tableData,
      label: "",
    },
  ];
};

export const getOverviewTableUnitPlansMemoized = getMemoizedLangFunction(
  getOverviewTableUnitPlans
);

const getMYPOverviewData = ({
  t,
  gradeInsightFieldsMap,
  showSubjectGradePairLabel,
  subjectGroupOverviewsAcrossGrades,
  subjectOverviewColumns,
  insightColumns,
  isPaginatedSGO,
  isSingleTableInsights,
  fieldListsV2,
}) => {
  return _.map(subjectGroupOverviewsAcrossGrades, subjectGroupOverview => {
    const unitPlanDetailsList = _.get(subjectGroupOverview, "unitPlans", []);

    const tableId =
      isSingleTableInsights && !isPaginatedSGO
        ? _.get(subjectGroupOverview, "grade.id")
        : getSubjectGradeId({ data: subjectGroupOverview });

    const tableLabel = isPaginatedSGO
      ? ""
      : getSubjectGradePairLabel({
          showSubjectGradePairLabel,
          overviewType: "VERTICAL_OVERVIEW",
          grade: subjectGroupOverview.grade,
          subject: subjectGroupOverview.subject,
        });
    const isArchived = _.get(subjectGroupOverview, "subject.isArchived", false);
    const insightFields = _.get(
      gradeInsightFieldsMap,
      `${subjectGroupOverview.grade.id}`,
      {}
    );

    const tableData = getTableDataWithConfiguredColumns({
      t,
      subjectOverviewColumns,
      insightColumns,
      insightFields,
      unitPlanDetailsList,
      fieldListsV2,
    });

    const emptyLabel = t("unitPlan:no_units_added");

    return {
      emptyLabel,
      id: tableId,
      value: tableData,
      label: tableLabel,
      isArchived,
    };
  });
};

export const generateOverviewTableDataList = ({
  t,
  curriculumType,
  subjectOverviewData,
  subjectOverviewColumns,
  gradeInsightFieldsMap,
  insightColumns,
  showSubjectGradePairLabel,
  subjectGroupOverviewsAcrossGrades,
  subjects,
  isPaginatedSGO,
  isSingleTableInsights,
  fieldListsV2,
}) => {
  switch (curriculumType) {
    case CURRICULUM_TYPE_MYP:
      return getMYPOverviewData({
        t,
        gradeInsightFieldsMap,
        insightColumns,
        showSubjectGradePairLabel,
        subjectGroupOverviewsAcrossGrades,
        subjectOverviewColumns,
        subjects,
        isPaginatedSGO,
        isSingleTableInsights,
        fieldListsV2,
      });

    case CURRICULUM_TYPE_UBD:
    default:
      return getUBDPYPOverviewData({
        t,
        subjectOverviewData,
        subjectOverviewColumns,
        gradeInsightFieldsMap,
        insightColumns,
        showSubjectGradePairLabel,
        fieldListsV2,
      });
  }
};

export const generateOverviewTableDataListMemoized = getMemoizedLangFunction(
  generateOverviewTableDataList
);

/**
 * MYP Insight Table Data Generation
 **/

const checkCanRowToggle = ({ node }) => {
  return node.children && !_.isEmpty(node.children);
};

const generateDpInsightHeaderRowData = ({
  firstColumnLabel,
  showSubjectsInHeader,
  subjects,
  label,
  t,
}) => {
  let headerColumns = [
    {
      id: `row-header-cell-addressed`,
      value: { title: "No. of times addressed" },
    },
  ];

  if (showSubjectsInHeader) {
    headerColumns = _.map(subjects, subject => {
      const subjectTags = _.join(
        _.map(_.get(subject, "variants"), "level.label"),
        ", "
      );
      const assessmentYearTitle = _.get(
        subject,
        "contentSubject.assessmentYearTitle"
      );
      const shouldJoinTagNTitle =
        !_.isEmpty(subjectTags) && !_.isEmpty(assessmentYearTitle);
      const subTitle = _.join(
        [assessmentYearTitle, subjectTags],
        shouldJoinTagNTitle ? " • " : ""
      );

      return {
        id: `row-header-cell-${_.get(subject, "id")}`,
        value: { title: _.get(subject, "name", ""), subTitle },
        style: { headerCellStyle: { minWidth: 220 } },
        showEllipsis: true,
        isHeaderArchived: _.get(subject, "isArchived", false),
      };
    });

    headerColumns = _.concat(headerColumns, [
      {
        id: `row-header-cell-total-count`,
        value: {
          title: t("unitPlan:total_units"),
        },
        style: { headerCellStyle: { minWidth: 220 } },
        tooltip: t("planningInsights:unique_units_across_grades_with_label", {
          label,
        }),
        isTotalUnitsColumn: true,
        showTotalUnitsSubtext: false,
      },
    ]);
  }

  return [
    {
      id: "row-header-cell",
      value: firstColumnLabel ? firstColumnLabel : "",
    },
    ...headerColumns,
  ];
};

const getTotalUnitsSubtext = ({
  isUbDSkills,
  isHorizontalOverview,
  t,
  elementType,
}) => {
  if (isHorizontalOverview) {
    if (isUbDSkills) {
      return t("planningInsights:unique_across_subjects");
    } else if (_.includes(ALLOWED_TYPES_HORIZONTAL_OVERVIEW, elementType)) {
      return t("planningInsights:unique_across_subjects");
    }
  } else if (_.includes(ALLOWED_TYPES_VERTICAL_OVERVIEW, elementType)) {
    return t("planningInsights:unique_across_grades_and_subjects");
  }
};

const getTotalUnitsTooltipText = ({
  isUbDSkills,
  isHorizontalOverview,
  label,
  t,
  elementType,
}) => {
  if (isHorizontalOverview) {
    if (isUbDSkills) {
      return t("planningInsights:unique_units_across_subjects_label", {
        label,
      });
    } else if (_.includes(ALLOWED_TYPES_HORIZONTAL_OVERVIEW, elementType)) {
      return t("planningInsights:unique_units_across_subjects_label", {
        label,
      });
    }
  } else if (_.includes(ALLOWED_TYPES_VERTICAL_OVERVIEW, elementType)) {
    return t("planningInsights:unique_units_across_grades_and_subjects_label", {
      label,
    });
  } else {
    return t("planningInsights:unique_units_across_grades_with_label", {
      label,
    });
  }
};

const generateMYPInsightHeaderRowData = ({
  elementType,
  overviewType,
  showSubjectGradePairLabel,
  columnList,
  truncateHeaderText,
  label,
  t,
}) => {
  const isHorizontalOverview = overviewType === "HORIZONTAL_OVERVIEW";
  const isUbDSkills = elementType === ELEMENT_TYPE_UBD_ATL;
  return [
    {
      id: "row-header-cell",
      value: "",
    },

    ..._.map(columnList, data => {
      const isArchived = _.get(data, "subject.isArchived", false);
      const labels = _.split(
        getSubjectGradePairLabel({
          grade: data.grade,
          subject: data.subject,
          overviewType,
          showSubjectGradePairLabel,
        }),
        " - "
      );

      const headerId = getSubjectGradeId({ data });

      return {
        id: `row-header-cell-${headerId}`,
        value: {
          title: _.first(labels),
          subTitle: _.size(labels) > 1 ? _.last(labels) : "",
        },

        showEllipsis: truncateHeaderText,
        isSubHeaderArchived: isArchived,
        isHeaderArchived: overviewType === "HORIZONTAL_OVERVIEW" && isArchived,
      };
    }),

    {
      id: `row-header-cell-total-count`,
      value: {
        title: t("unitPlan:total_units"),
      },
      style: { headerCellStyle: { minWidth: 220 } },
      tooltip: getTotalUnitsTooltipText({
        isUbDSkills,
        isHorizontalOverview,
        label,
        t,
        elementType,
      }),
      totalUnitTitle: getTotalUnitsSubtext({
        isUbDSkills,
        isHorizontalOverview,
        t,
        elementType,
      }),
      isTotalUnitsColumn: true,
    },
  ];
};

const getInsightCountColumn = ({
  id,
  unitPlanIds,
  totalCount,
  depth,
  emptyCellDepth,
  showEmptyCell,
  backgroundGrey,
  isArchived,
  style,
}) => {
  return {
    id,
    value: showEmptyCell && _.isEqual(depth, emptyCellDepth) ? "" : totalCount,
    unitPlanIds,
    highlightCell: false,
    backgroundGrey,
    style,
    isArchived,
  };
};

const generateDpInsightBodyRowsData = ({
  nodes,
  elementType,
  unitPlanInsights,
  isHorizontalOverview,
  subjectId,
  emptyCellDepth,
  showEmptyCell,
  subjects,
  canToggle,
}) => {
  const isNested = _.some(nodes, node => !_.isEmpty(node?.children));
  const toggleView = _.some(
    nodes,
    node =>
      canToggle &&
      checkCanRowToggle({
        node,
      })
  );
  return _.map(nodes, node => {
    const { id, depth, parent } = node;
    const canRowToggle =
      canToggle &&
      checkCanRowToggle({
        node,
      });

    const style = getInsightTableStyles({
      depth,
      elementType,
      isNested,
      canToggle: toggleView,
    });

    const backgroundGrey =
      (!canRowToggle && depth == 1 && isNested) ||
      (showEmptyCell && _.isEqual(depth, emptyCellDepth));

    const unitPlanInsight = _.get(unitPlanInsights, `${id}`, {});
    const countColumnProps = {
      depth,
      emptyCellDepth,
      style,
      showEmptyCell,
      backgroundGrey,
    };

    const plannerNode = _.find(unitPlanInsights, item =>
      _.isEqual(_.get(item, "plannerNode.id"), id)
    );

    let countColumns = [
      getInsightCountColumn({
        id: `${id}-${subjectId}`,
        totalCount: _.get(unitPlanInsight, "totalCount", 0),
        unitPlanIds: _.map(
          _.get(unitPlanInsight, "unitPlans", []),
          unit => unit.id
        ),
        ...countColumnProps,
      }),
    ];

    if (isHorizontalOverview) {
      countColumns = _.map(subjects, subject => {
        const edge = _.find(_.get(plannerNode, "edges", []), insight => {
          return _.isEqual(_.get(subject, "id"), _.get(insight, "subject.id"));
        });

        const insightCountId = getSubjectGradeId({ edge });

        return getInsightCountColumn({
          id: `${node.id}-${insightCountId}`,
          totalCount: _.get(edge, "totalCount", "-"),
          unitPlanIds: _.map(_.get(edge, "unitPlans", []), "id"),
          ...countColumnProps,
          isArchived: _.get(subject, "isArchived", false),
        });
      });

      countColumns = _.concat(countColumns, [
        {
          id: `${id}-totalCount`,
          value: _.get(plannerNode, "unitPlanData.totalCount", "-"),
          backgroundGrey,
          unitPlanIds: _.map(
            _.get(plannerNode, "unitPlanData.unitPlans", []),
            item => item.id
          ),
          style,
        },
      ]);
    }

    return [
      {
        id,
        parent,
        canToggle: canRowToggle,
        children: node.children,
        backgroundGrey,
        isLeaf: node.children && _.isEmpty(node.children),
        value: _.get(node, "label", ""),
        code: node?.code,
        style,
        plannerElementType: elementType,
      },
      ...countColumns,
    ];
  });
};

const generateMypUbdInsightBodyRowsData = ({
  nodes,
  elementType,
  overviewType,
  subjectGroupId,
  insightCountData,
  columnList,
  canToggle = true,
}) => {
  const isNested = _.some(nodes, node => !_.isEmpty(node?.children));
  const toggleView = _.some(
    nodes,
    node =>
      canToggle &&
      checkCanRowToggle({
        node,
      })
  );
  return _.map(nodes, node => {
    const { id, depth, type, parent } = node;

    const plannerNode = _.find(
      insightCountData,
      item => item.plannerNode.id == id && item.plannerNode.type == type
    );

    const canRowToggle =
      canToggle &&
      checkCanRowToggle({
        node,
      });

    const style = getInsightTableStyles({
      depth,
      elementType,
      isNested,
      canToggle: toggleView,
    });

    const backgroundGrey =
      !canRowToggle &&
      depth == 2 &&
      isNested &&
      _.includes(["MYP_KEY_CONCEPT", "MYP_OBJECTIVES"], elementType);

    //We have Rows with just subject Group Name in MYP Key Concepts
    if (node.showRowWithGroupLabel) {
      const value = _.get(node, "rowLabel", "");
      return [
        {
          id: `subjectGroupName-${value}`,
          parent: null,
          canToggle: false,
          children: [],
          isLeaf: false,
          value,
          style,
          backgroundGrey: true,
        },
        // for all columns + unique units column
        ..._.map(Array(_.size(columnList) + 1), () => {
          const totalCount = " ";

          return {
            id: `emptyCell`,
            value: totalCount,
            unitPlanIds: [],
            highlightCell: false,
            style,
            backgroundGrey: true,
            isArchived: _.get(columnList, "subject.isArchived", false),
          };
        }),
      ];
    }

    return [
      {
        id,
        parent,
        canToggle: canRowToggle,
        children: node.children,
        isLeaf: node.children && _.isEmpty(node.children),
        value: _.get(node, "label", ""),
        backgroundGrey,
        style,
      },
      ..._.map(columnList, data => {
        const subject = _.get(data, "subject", null);
        const grade = _.get(data, "grade", null);

        let edge = _.find(
          _.get(plannerNode, "edges", []),
          item => item.subject.id == subject.id
        );

        if (grade) {
          edge = _.find(
            _.get(plannerNode, "edges", []),
            item => item.subject.id == subject.id && item.grade.id == grade.id
          );
        }

        const totalCount = _.get(
          edge,
          "unitPlanData.totalCount",
          _.get(edge, "totalCount", "-")
        );

        const unitPlanIds = _.get(edge, "unitPlanData.unitPlans", null)
          ? _.map(_.get(edge, "unitPlanData.unitPlans", []), unit => unit.id)
          : _.map(_.get(edge, "unitPlans", []), unit => unit.id);

        const cellId = getSubjectGradeId({ data });

        return {
          id: `${id}-${cellId}`,
          value: totalCount,
          unitPlanIds,
          backgroundGrey,
          highlightCell:
            _.isEqual(overviewType, "HORIZONTAL_OVERVIEW") &&
            _.isEqual(
              _.get(data.subject, "subjectGroup.id", ""),
              subjectGroupId
            ),
          style,
          isArchived: _.get(subject, "isArchived", false),
        };
      }),
      {
        id: `${id}-totalCount`,
        value: _.get(plannerNode, "unitPlanData.totalCount", "-"),
        unitPlanIds: _.map(
          _.get(plannerNode, "unitPlanData.unitPlans", []),
          item => item.id
        ),
        backgroundGrey,
        highlightCell: false,
        style,
      },
    ];
  });
};

const generateInsightTableData = ({
  nodes,
  elementType,
  overviewType,
  subjectGroupId,
  insightCountData,
  showSubjectGradePairLabel,
  truncateHeaderText = false,
  columnList,
  t,
  canToggle,
  label,
  isUbd,
}) => {
  const table = [];

  const headerRowData = generateMYPInsightHeaderRowData({
    elementType,
    overviewType,
    columnList,
    showSubjectGradePairLabel,
    truncateHeaderText,
    t,
    label,
    isUbd,
  });

  const bodyRowsData = generateMypUbdInsightBodyRowsData({
    nodes,
    elementType,
    overviewType,
    subjectGroupId,
    insightCountData,
    columnList,
    canToggle,
  });

  return _.concat(table, [headerRowData], bodyRowsData);
};

const generateDpInsightTableData = ({
  label,
  nodes,
  elementType,
  unitPlanInsights,
  subjectId,
  emptyCellDepth,
  showEmptyCell,
  showPaddingLeft,
  firstColumnLabel,
  isHorizontalOverview = false,
  subjects,
  selectedSubjectIds,
  t,
  canToggle,
}) => {
  const table = [];

  const selectedSubjects = _.filter(subjects, subject =>
    _.includes(selectedSubjectIds, _.get(subject, "id"))
  );

  const headerRowData = generateDpInsightHeaderRowData({
    elementType,
    firstColumnLabel,
    showSubjectsInHeader: isHorizontalOverview,
    subjects: selectedSubjects,
    label,
    t,
  });

  const bodyRowsData = generateDpInsightBodyRowsData({
    nodes,
    elementType,
    unitPlanInsights,
    isHorizontalOverview,
    subjects: selectedSubjects,
    subjectId,
    emptyCellDepth,
    showEmptyCell,
    showPaddingLeft,
    canToggle,
  });

  return _.concat(table, [headerRowData], bodyRowsData);
};

const getUpdatedInsightData = ({ verticalInsight }) => {
  return _.map(verticalInsight, insight => {
    return {
      plannerNode: _.get(insight, "element", {}),
      edges: _.map(insight["edges"], edge => {
        const unitPlans = _.get(edge, "unitPlans", []);
        const assessments = _.get(edge, "assessments", []);
        const questions = _.get(edge, "questions", []);

        return {
          grade: _.get(edge, "grade"),
          unitPlanData: {
            unitPlans,
            totalCount: _.isEqual(_.size(unitPlans), 0)
              ? "-"
              : _.size(unitPlans),
          },
          assessmentData: {
            assessments,
            totalCount: _.isEqual(_.size(assessments), 0)
              ? "-"
              : _.size(assessments),
          },
          questionsData: {
            questions,
            totalCount: _.isEqual(_.size(questions), 0)
              ? "-"
              : _.size(questions),
          },
        };
      }),
    };
  });
};

export const getUpdatedInsightDataMemoized = _.memoize(
  params => getUpdatedInsightData(params),
  params => JSON.stringify(params)
);

export const getUpdatedStandardsInsightData = ({
  insightData,
  isSubjectPOI = false,
  selectedGradeIds = [],
}) => {
  return _.map(insightData, insight => {
    const insightCountData = _.reduce(
      insight["edges"],
      (result, edge) => {
        // TODO: remove this when backend is updated
        // currently, applying grade filter does not return updated
        // data from backend
        if (isSubjectPOI && !_.isEmpty(selectedGradeIds)) {
          const grade = _.get(edge, "grade");
          if (!_.includes(selectedGradeIds, grade.id)) {
            return result;
          }
        }

        const unitPlans = _.get(
          edge,
          isSubjectPOI ? "unitPlanData.unitPlans" : "unitPlans",
          []
        );
        const assessments = _.get(
          edge,
          isSubjectPOI ? "assessmentData.assessments" : "assessments",
          []
        );
        const questions = _.get(
          edge,
          isSubjectPOI ? "questionsData.questions" : "questions",
          []
        );

        const oldUnitPlans = _.get(result, "unitPlanData.unitPlans", []);
        const oldAssessments = _.get(result, "assessmentData.assessments", []);
        const oldQuestions = _.get(result, "questionsData.questions", []);

        const newUnitPlans = _.uniqBy([...oldUnitPlans, ...unitPlans], "id");
        const newAssessments = _.uniqBy(
          [...oldAssessments, ...assessments],
          "id"
        );
        const newQuestions = _.uniqBy([...oldQuestions, ...questions], "id");

        result.unitPlanData = {
          unitPlans: newUnitPlans,
          totalCount: _.size(newUnitPlans),
        };

        result.assessmentData = {
          assessments: newAssessments,
          totalCount: _.size(newAssessments),
        };

        result.questionsData = {
          questions: newQuestions,
          totalCount: _.size(newQuestions),
        };

        // if no unit plans or assessments, set totalCount to "-"
        if (_.isEqual(_.size(newUnitPlans), 0)) {
          result.unitPlanData.totalCount = "-";
        }
        if (_.isEqual(_.size(newAssessments), 0)) {
          result.assessmentData.totalCount = "-";
        }
        if (_.isEqual(_.size(newQuestions), 0)) {
          result.questionsData.totalCount = "-";
        }

        return result;
      },
      {
        unitPlanData: {
          unitPlans: [],
          totalCount: "-",
        },
        assessmentData: {
          assessments: [],
          totalCount: "-",
        },
        questionsData: {
          questions: [],
          totalCount: "-",
        },
      }
    );

    return {
      plannerNode: _.get(insight, isSubjectPOI ? "plannerNode" : "element", {}),
      insightCountData,
    };
  });
};

const generateStandardsViewTableData = ({
  nodesTableWise,
  tablesList,
  t,
  elementLabel,
  isSubjectView,
  curriculumType,
  elementType,
}) => {
  const isWorksheetsEnabled = getIsWorksheetsEnabled();

  const headerRowData = [
    {
      id: "row-header-cell",
      value: "",
    },
    {
      id: `row-header-cell-addressed-unitPlan`,
      value: {
        upperSubTitle: t("common:no_of_times_addressed_in"),
        title: t("common:unit_plural"),
      },
      style: {
        headerCellStyle: { width: 280 },
      },
    },
    {
      id: `row-header-cell-addressed-assessments`,
      value: {
        upperSubTitle: t("common:no_of_times_addressed_in"),
        title: t("common:task_plural"),
      },
      style: {
        headerCellStyle: { width: 280 },
      },

      tooltip: _.isEqual(curriculumType, CURRICULUM_TYPE_PYP)
        ? t("common:le_label_plural")
        : t("journal:le_and_assessments"),
    },
  ];

  if (isWorksheetsEnabled) {
    headerRowData.push({
      id: `row-header-cell-addressed-assessments`,
      value: {
        upperSubTitle: t("common:no_of_times_addressed_in"),
        title: t("common:question"),
      },
      style: {
        headerCellStyle: { width: 280 },
      },
    });
  }

  const tables = _.map(tablesList, (tableDetails, index) => {
    const gradeNodes = _.get(nodesTableWise, `${index}`, []);

    const isNested = _.some(gradeNodes, node => !_.isEmpty(node?.children));
    const toggleView = _.some(gradeNodes, node =>
      checkCanRowToggle({
        node,
      })
    );
    const bodyRowsData = _.map(gradeNodes, node => {
      const { id, depth, parent, children } = node;

      const canRowToggle = isSubjectView
        ? false
        : checkCanRowToggle({
            node: node,
          });

      const style = getInsightTableStyles({
        depth,
        isNested,
        canToggle: toggleView,
        elementType,
        isCollapsible: !isSubjectView,
      });

      const { grade, insightData = [] } = tableDetails;

      const insightCountData = _.find(insightData, data =>
        _.isEqual(_.get(data, "plannerNode.id"), id)
      );

      // if there's only root nodes, don't grey all out
      const multiLevelNodes = _.some(
        gradeNodes,
        node => _.has(node, "children") && !_.isEmpty(node.children)
      );
      const backgroundGrey = isSubjectView && !parent && multiLevelNodes;

      const edges = _.get(insightCountData, "edges", []);

      const countColumns = [];

      const EDGE_TYPES = [
        {
          key: "unitPlanData",
          type: "unitPlans",
          idKey: "unitPlanIds",
        },
        { key: "assessmentData", type: "assessments", idKey: "assessmentIds" },
        ...(isWorksheetsEnabled
          ? [
              {
                key: "questionsData",
                type: "questions",
                idKey: "questionIds",
              },
            ]
          : []),
      ];

      const edge = _.find(edges, edge =>
        _.isEqual(_.get(edge, "grade.id"), grade.id)
      );

      // we need two columns per grade, unit plans and assessments count
      _.forEach(EDGE_TYPES, edgeType => {
        const { type, key, idKey } = edgeType;
        const totalCount = _.get(edge, [key, "totalCount"], "-");

        const countColumnData = {
          id: `${_.get(grade, "id")}-${key}`,
          value: totalCount,
          [idKey]: _.map(_.get(edge, [key, type], []), "id"),
          highlightCell: false,
          backgroundGrey,
          style,
        };

        // questions have further nesting
        if (type === "questions") {
          const questions = _.get(edge, [key, type], []);

          countColumnData[idKey] = _.reduce(
            questions,
            (result, question) => {
              const worksheetIds = _.map(question?.assessmentDataV2, "id");
              return _.union(result, worksheetIds);
            },
            []
          );
        }

        countColumns.push(countColumnData);
      });

      return [
        // first column - planner node
        {
          id,
          plannerElementType: elementType,
          parent: isSubjectView ? null : parent,
          canToggle: canRowToggle,
          children,
          isLeaf: children && _.isEmpty(children),
          value: _.get(node, "label", ""),
          code: node?.code,
          subText: node?.subText,
          backgroundGrey,
          style,
        },
        ...countColumns,
      ];
    });

    if (!_.isEmpty(bodyRowsData)) {
      const tableData = [headerRowData, ...bodyRowsData];

      return {
        tableId: _.get(tableDetails, "id", ""),
        tableName: _.get(tableDetails, "name", ""),
        tableData,
        isArchived: _.get(tableDetails, "isArchived", false),
      };
    } else {
      const tableData = [
        headerRowData,
        [
          {
            id: `empty-body-data-rows-${_.get(tableDetails, "id", "")}`,
            value: t("planningInsights:no_configured_yet", {
              label: elementLabel,
            }),
            backgroundGrey: true,
            isEmpty: true,
            colSpan: isWorksheetsEnabled ? 4 : 3,
          },
        ],
      ];

      return {
        tableId: _.get(tableDetails, "id", ""),
        tableName: _.get(tableDetails, "name", ""),
        tableData,
        isArchived: _.get(tableDetails, "isArchived", false),
      };
    }
  });

  return tables;
};

export const getSubjectStandardsSingleTableData = ({
  plannerElementSet,
  elementType,
  elementLabel,
  curriculumType,
  insightData,
  t,
}) => {
  const isWorksheetsEnabled = getIsWorksheetsEnabled();

  const headerRowData = [
    {
      id: "row-header-cell",
      value: "",
    },
    {
      id: `row-header-cell-addressed-unitPlan`,
      value: {
        upperSubTitle: t("common:no_of_times_addressed_in"),
        title: t("common:unit_plural"),
      },
      style: {
        headerCellStyle: { width: 280 },
      },
    },
    {
      id: `row-header-cell-addressed-assessments`,
      value: {
        upperSubTitle: t("common:no_of_times_addressed_in"),
        title: t("common:task_plural"),
      },
      style: {
        headerCellStyle: { width: 280 },
      },

      tooltip: _.isEqual(curriculumType, CURRICULUM_TYPE_PYP)
        ? t("common:le_label_plural")
        : t("journal:le_and_assessments"),
    },
  ];

  if (isWorksheetsEnabled) {
    headerRowData.push({
      id: `row-header-cell-addressed-assessments`,
      value: {
        upperSubTitle: t("common:no_of_times_addressed_in"),
        title: t("common:question"),
      },
      style: {
        headerCellStyle: { width: 280 },
      },
    });
  }

  const plannerElementSetNodes = _.get(plannerElementSet, "nodes", []);

  // at depth 0, there is a root node of the subject
  // we don't need to display it
  const nodesWithoutSubjectNode = sortFlatNodes({
    nodes: plannerElementSetNodes,
  });

  const isNested = _.some(
    nodesWithoutSubjectNode,
    node => !_.isEmpty(node?.children)
  );

  const bodyRowsData = _.map(nodesWithoutSubjectNode, node => {
    const { id, depth, parent, children } = node;

    const canRowToggle = checkCanRowToggle({
      node: node,
    });

    const style = getInsightTableStyles({
      depth,
      isNested,
      canToggle: isNested,
      elementType,
      isCollapsible: true,
    });

    const plannerNodeData = _.find(insightData, data =>
      _.isEqual(_.get(data, "plannerNode.id"), id)
    );

    const subjectNodeId = _.find(
      plannerElementSetNodes,
      data => !data.parent && data.depth === 0
    )?.id;

    const insightCountData = _.get(plannerNodeData, "insightCountData", {});

    const countColumns = [];

    const EDGE_TYPES = [
      {
        key: "unitPlanData",
        type: "unitPlans",
        idKey: "unitPlanIds",
      },
      { key: "assessmentData", type: "assessments", idKey: "assessmentIds" },
      ...(isWorksheetsEnabled
        ? [
            {
              key: "questionsData",
              type: "questions",
              idKey: "questionIds",
            },
          ]
        : []),
    ];

    // we need two columns per grade, unit plans and assessments count
    _.forEach(EDGE_TYPES, edgeType => {
      const { type, key, idKey } = edgeType;
      const totalCount = _.get(insightCountData, [key, "totalCount"], "-");

      const countColumnData = {
        id: `${id}-${key}`,
        value: totalCount,
        [idKey]: _.map(_.get(insightCountData, [key, type], []), "id"),
        highlightCell: false,
        backgroundGrey: false,
        style,
      };

      // questions have further nesting
      if (type === "questions") {
        const questions = _.get(insightCountData, [key, type], []);
        countColumnData[idKey] = _.reduce(
          questions,
          (result, question) => {
            const worksheetIds = _.map(question?.assessmentDataV2, "id");
            return _.union(result, worksheetIds);
          },
          []
        );
      }

      countColumns.push(countColumnData);
    });

    return [
      // first column - planner node
      {
        id,
        plannerElementType: elementType,
        // if it's a subject node, set parent to null
        // so that it is treated like a root node
        parent: subjectNodeId && parent === subjectNodeId ? null : parent,
        canToggle: canRowToggle,
        children,
        isLeaf: children && _.isEmpty(children),
        value: _.get(node, "label", ""),
        code: node?.code,
        subText: node?.subText,
        backgroundGrey: false,
        style,
      },
      ...countColumns,
    ];
  });

  if (!_.isEmpty(bodyRowsData)) {
    return [headerRowData, ...bodyRowsData];
  }

  // if no data present, return empty table
  return [
    headerRowData,
    [
      {
        id: `empty-body-data-rows-${elementType}`,
        value: t("planningInsights:not_configured_yet_for_subject_and_grade", {
          label: elementLabel,
        }),
        backgroundGrey: true,
        isEmpty: true,
        colSpan: isWorksheetsEnabled ? 4 : 3,
      },
    ],
  ];
};

export const CELL_VALUE_ICONS = {
  ICON_TICK: <TickLargeOutlined variant="success" />,
  ICON_CROSS: <CloseOutlined variant="subtle" />,
};

const getProgrammeInsightColumnProps = ({ totalCount, elementType, style }) => {
  const isTotalCountZero = _.isEqual(totalCount, 0);
  switch (elementType) {
    case "PYP_FOCUS_AREA": {
      const updatedStyle = { ...style };
      updatedStyle.dataCellStyle = {
        backgroundColor: isTotalCountZero ? "unset" : colors.surfaceSuccess,
      };

      return {
        // due to memoization, icons cause cyclic JSON issue
        // so can not directly put icons as cell value
        value: isTotalCountZero ? "ICON_CROSS" : "ICON_TICK",
        isIcon: true,
        style: updatedStyle,
      };
    }

    default:
      return {
        value: totalCount,
        style,
      };
  }
};

const generatePYPProgammeInsightsTableData = ({
  tableList,
  grades,
  selectedGradeIds,
  themes,
  nodes,
  programmeGradeInsights,
  programmeThemeInsights,
  elementType,
  truncateHeaderText = false,
  label,
  t,
}) => {
  const programmeInsights = {
    gradeInsight: programmeGradeInsights,
    themeInsight: programmeGradeInsights,
  };

  const selectedGrades = _.filter(grades, grade =>
    _.includes(selectedGradeIds, grade.id)
  );

  const tables = _.map(tableList, table => {
    const {
      type,
      title,
      id: tableId,
      subTitle,
      isDataLoading,
      showFilter = false,
    } = table;

    const isGradeInsights = _.isEqual(type, "gradeInsight");
    const columnList = isGradeInsights ? selectedGrades : themes;

    const insightCountData = _.find(_.get(programmeInsights, type, {}), {
      type: elementType,
    })?.insights;

    const headerRowsData = [
      {
        id: "row-header-cell",
        value: "",
      },
      ..._.map(columnList, columnData => {
        const { id } = columnData;
        return {
          id: `row-header-cell-${id}`,
          value: {
            title: isGradeInsights ? columnData.name : columnData.label,
          },
          style: { headerCellStyle: { whiteSpace: "nowrap" } },
          showEllipsis: truncateHeaderText,
        };
      }),
      {
        id: `row-header-cell-total-count`,
        value: {
          title: t("unitPlan:total_units"),
        },
        style: { headerCellStyle: { minWidth: 220 } },
        tooltip: t("planningInsights:unique_units_across_grades_with_label", {
          label,
        }),
        isTotalUnitsColumn: true,
        showTotalUnitsSubtext: isGradeInsights ? true : false,
      },
    ];
    const isNested = _.some(nodes, node => !_.isEmpty(node?.children));

    const toggleView = _.some(nodes, node =>
      checkCanRowToggle({
        node,
      })
    );

    const bodyRowsData = _.map(nodes, node => {
      const { id, depth, parent, isArchived = false } = node;

      const canRowToggle = checkCanRowToggle({
        node,
      });

      const style = getInsightTableStyles({
        depth,
        elementType,
        isNested,
        canToggle: toggleView,
      });

      let edge = _.find(insightCountData, ["element.id", id]);

      if (_.isEqual(elementType, "PYP_SUBJECT")) {
        edge = _.find(insightCountData, ["subject.id", id]);
      }

      edge = edge?.programmeInsightEdge;

      const uniqueUnitPlans = isGradeInsights
        ? _.get(edge, "gradeUnitPlans", [])
        : _.get(edge, "themeUnitPlans", []);
      const uniqueUnitsCount = _.size(uniqueUnitPlans);

      // in theme descriptors, we need a grey row for the parent theme label
      if (node.showThemeRow) {
        return [
          {
            id: `themeRow-${id}`,
            parent: null,
            canToggle: false,
            children: [],
            isLeaf: false,
            value: _.get(node, "label", ""),
            backgroundGrey: true,
            style,
          },
          // for all columns + unique units column
          ..._.map(Array(_.size(columnList) + 1), () => {
            return {
              id: `emptyCell`,
              value: " ",
              unitPlanIds: [],
              highlightCell: false,
              backgroundGrey: true,
              style,
            };
          }),
        ];
      }

      return [
        // first column - planner element
        {
          id,
          parent,
          canToggle: canRowToggle,
          children: node.children,
          isLeaf: node.children && _.isEmpty(node.children),
          value: _.get(node, "label", _.get(node, "name", "")),
          plannerElementType: elementType,
          style,
          isArchived,
        },
        // grade or theme insight rows
        ..._.map(columnList, columnData => {
          const insightData = _.find(_.get(edge, type, []), [
            isGradeInsights ? "grade.id" : "theme.id",
            columnData.id,
          ]);

          const unitPlans = _.get(insightData, "unitPlans", []);
          const totalCount = _.size(unitPlans);

          return {
            id: `${id}-${columnData.id}`,
            unitPlanIds: _.map(unitPlans, "id"),
            highlightCell: false,
            // for Programme overview, we exclude unit from a single grade at a time
            ...(isGradeInsights
              ? {
                  isExcludingFromGrade: true,
                  grade: columnData,
                }
              : {}),
            ...getProgrammeInsightColumnProps({
              totalCount,
              elementType,
              style,
            }),
          };
        }),
        // unique units count
        {
          id: `${id}-totalCount`,
          value: uniqueUnitsCount,
          unitPlanIds: _.map(uniqueUnitPlans, "id"),
          highlightCell: false,
          style,
        },
      ];
    });

    return {
      tableId,
      tableName: t(title, { label }),
      tableSubtext: subTitle ? t(subTitle, { label }) : null,
      tableData: [headerRowsData, ...bodyRowsData],
      showFilter,
      isDataLoading,
    };
  });

  return tables;
};

export const generateInsightTableDataMemoized = getMemoizedLangFunction(
  generateInsightTableData
);

export const generateDpInsightTableDataMemoized = getMemoizedLangFunction(
  generateDpInsightTableData
);

export const generateStandardsViewTableDataMemoized = getMemoizedLangFunction(
  generateStandardsViewTableData
);

export const generatePYPProgammeInsightsTableDataMemoized = getMemoizedLangFunction(
  generatePYPProgammeInsightsTableData
);

const generateOverallInsightsTableData = ({
  t,
  nodes,
  insightCountData,
  columnList,
  elementType,
  label,
}) => {
  const headerRows = [
    // first column
    {
      id: "row-header-cell",
      value: "",
    },
    // grade columns
    ..._.map(columnList, column => {
      return {
        id: `row-header-cell-${column.id}`,
        value: {
          title: column.name,
        },
      };
    }),
    // unique units count column
    {
      id: `row-header-cell-total-count`,
      value: {
        title: t("unitPlan:total_units"),
      },
      style: { headerCellStyle: { minWidth: 220 } },
      tooltip: t("planningInsights:unique_units_across_grades_with_label", {
        label,
      }),
      isTotalUnitsColumn: true,
    },
  ];
  const isNested = _.some(nodes, node => !_.isEmpty(node?.children));
  const toggleView = _.some(nodes, node =>
    checkCanRowToggle({
      node,
    })
  );
  const bodyRows = _.map(nodes, node => {
    const { id, depth, parent } = node;

    const insightDataNode = _.find(insightCountData, item =>
      _.isEqual(_.get(item, "plannerNode.id"), id)
    );

    const insights = _.get(insightDataNode, "insights", []);
    const totalUnitPlanData = _.get(insightDataNode, "totalUnitPlanData", {});

    const canRowToggle = checkCanRowToggle({
      node,
    });

    const style = getInsightTableStyles({
      depth,
      elementType,
      canToggle: toggleView,
    });

    const uniqueUnitsCount = _.get(totalUnitPlanData, "totalCount", "-");

    return [
      // first column
      {
        id,
        parent,
        canToggle: canRowToggle,
        children: node.children,
        isLeaf: node.children && _.isEmpty(node.children),
        value: _.get(node, "label", ""),
        // no grey rows in overall insights
        backgroundGrey: false,
        style,
      },
      // grade columns
      ..._.map(columnList, column => {
        const insightData = _.find(insights, ["grade.id", column.id]);
        const totalCount = _.get(insightData, "unitPlanData.totalCount", "-");
        const unitPlanIds = _.map(
          _.get(insightData, "unitPlanData.unitPlans"),
          "id"
        );

        return {
          id: `${id} - ${column.id}`,
          value: totalCount,
          unitPlanIds,
          highlightCell: false,
          backgroundGrey: false,
          style,
        };
      }),
      // unique counts column
      {
        id: `${id}-totalCount`,
        value: uniqueUnitsCount,
        unitPlanIds: _.map(totalUnitPlanData?.unitPlans, "id"),
        highlightCell: false,
        backgroundGrey: !canRowToggle && depth == 1 && isNested,
        style,
      },
    ];
  });

  return [headerRows, ...bodyRows];
};

export const generateOverallInsightsTableDataMemoized = getMemoizedLangFunction(
  generateOverallInsightsTableData
);

const getChecklistLabelValue = ({ value, options, t }) => {
  if (_.isEmpty(value)) return t("common:none");
  return _.isEqual(_.size(value), _.size(options)) && !_.isEmpty(options)
    ? t("common:all")
    : _.map(value, item => {
        return _.find(options, {
          value: item,
        })?.label;
      });
};

export const getChecklistLabelValueMemoized = getMemoizedLangFunction(
  getChecklistLabelValue
);

const getDPOverviewItem = ({ insightType, currentCurriculumProgram }) => {
  let overviewType, plannerElementTypes, currentInsightItem;

  _.forEach(
    getCurriculumOverviewWithCustomDatasets({ currentCurriculumProgram }),
    overviewItem => {
      const children = _.get(overviewItem, "children", []);
      const foundInsight = _.find(children, { value: insightType });

      if (foundInsight) {
        overviewType = _.get(overviewItem, "type");
        currentInsightItem = foundInsight;
        plannerElementTypes = _.get(
          currentInsightItem,
          "plannerElementTypes",
          []
        );
        return;
      }
    }
  );

  return {
    overviewType,
    currentInsightItem,
    plannerElementTypes,
  };
};

export const getDPOverviewItemMemoized = _.memoize(
  params => getDPOverviewItem(params),
  props => JSON.stringify(props)
);

// CAS and EE in DP core & SLR and RP in CP core do not have unit planning enabled
// hence we can't show insights for these subjects
const CURRICULUM_SUBJECT_TYPE_FILTERS = {
  [CURRICULUM_TYPE_DP]: [
    SUBJECT_TYPE_GENERIC,
    SUBJECT_TYPE_OTHERS,
    SUBJECT_TYPE_TOK,
    SUBJECT_TYPE_LD,
    SUBJECT_TYPE_PPS,
  ],
};

const getInsightSubjectTypeFilter = ({ curriculumType }) => {
  const types = _.get(CURRICULUM_SUBJECT_TYPE_FILTERS, curriculumType, []);

  return {
    types,
  };
};

export const getInsightSubjectTypeFilterMemoized = _.memoize(
  params => getInsightSubjectTypeFilter(params),
  props => JSON.stringify(props)
);

export const onClickExcludeUnitPlan = async ({
  isModalOpen,
  dontShowExcludeUnitDialog,
  refetchQueries,
  updateExcludeUnit,
  updateUnitPlanInclusionStatus,
  unitPlan,
  grade,
  isExcludingFromGrade,
}) => {
  if (!dontShowExcludeUnitDialog && !isModalOpen) {
    updateExcludeUnit({
      modalOpen: true,
      unitPlan,
      grade,
      isExcludingFromGrade,
    });
  } else if (dontShowExcludeUnitDialog || isModalOpen) {
    const unitPlanId = unitPlan?.id;
    const gradeId = grade?.id;

    // for PYP Programme overview, we exclude from a grade
    // in case of POI and columns that show grade insights
    const excludedUnits = isExcludingFromGrade
      ? [{ unitPlanId, grades: gradeId ? [gradeId] : [] }]
      : [unitPlanId];

    // reset modal state
    if (isModalOpen) {
      updateExcludeUnit({
        modalOpen: false,
        unitPlan: null,
        grade: {},
        isExcludingFromGrade: false,
      });
    }

    await updateUnitPlanInclusionStatus({
      includedUnits: [],
      excludedUnits,
      shouldRefetchQuery: !_.isEmpty(refetchQueries),
      refetchQueries,
      isExcludingFromGrade,
      grade,
    });
  }
};

export const updateUnitPlanInclusionStatus = params => {
  const {
    includedUnits,
    excludedUnits,
    shouldRefetchQuery = false,
    refetchQueries,
    isExcludingFromGrade = false,
    grade,
    unitPlanFeedFilters,
  } = params;
  return async (dispatch, getState) => {
    const state = getState();
    let toastMessage, localeParams;

    const isIncludingUnits = !_.isEmpty(includedUnits),
      isExcludingUnits = !_.isEmpty(excludedUnits);
    if (isIncludingUnits && isExcludingUnits) {
      toastMessage = "toastMsgs:units_included_and_excluded_from_insight_views";
      localeParams = [
        {
          key: "includedCount",
          value: _.size(includedUnits),
          isPlainText: true,
        },
        {
          key: "excludedCount",
          value: _.size(excludedUnits),
          isPlainText: true,
        },
      ];
    } else if (isIncludingUnits) {
      toastMessage = "toastMsgs:units_included_in_insight_views";
      localeParams = [
        { key: "count", value: _.size(includedUnits), isPlainText: true },
      ];
    } else if (isExcludingUnits) {
      const exclusionCount = _.size(excludedUnits);
      // TODO: change this for all toast messages when changing inclusion flow
      toastMessage =
        isExcludingFromGrade && _.isEqual(exclusionCount, 1)
          ? "toastMsgs:units_excluded_from_programme_overview_from_grade"
          : "toastMsgs:units_excluded_from_insight_views";

      localeParams = [
        {
          key: "count",
          value: exclusionCount,
          isPlainText: true,
        },
        {
          key: "gradeName",
          value: _.get(grade, "name", ""),
          isPlainText: true,
        },
      ];
    }

    const curriculumProgramId = _.get(
      state,
      "platform.currentCurriculumProgram.id",
      null
    );

    const organizationId = _.get(state, "login.userInfo.org_id", null);

    const selectedAcademicYear = _.get(
      state,
      "platform.academicYearSelected",
      getCurrentAcademicYear({ organizationId })
    );

    const academicYear =
      state.poiAdmin?.academicYear || selectedAcademicYear.id;

    const curriculumType = _.get(
      state,
      "platform.currentCurriculumProgram.type",
      ""
    );

    // updateUnitPlanInclusionInGradeMutation is only called for PYP Progamme Overview
    // where grade is in context i.e in POI & columns with insight of grades
    const mutation = isExcludingFromGrade
      ? updateUnitPlanInclusionInGradeMutation
      : updateUnitPlanInclusionStatusMutation;

    const input = isExcludingFromGrade
      ? { included: includedUnits, excluded: excludedUnits }
      : {
          includedIds: includedUnits,
          excludedIds: excludedUnits,
          filters: unitPlanFeedFilters,
        };

    try {
      await client.mutate({
        mutation,
        variables: {
          input,
        },
        refetchQueries: shouldRefetchQuery
          ? [
              {
                query: getInsightsIncludedUnitCountQuery,
                variables: {
                  id: organizationId,
                  academicYears: academicYear ? [academicYear] : [],
                  curriculumProgramIds: curriculumProgramId
                    ? [curriculumProgramId]
                    : [],
                  fetchTransdisciplinaryCount: _.isEqual(
                    curriculumType,
                    CURRICULUM_TYPE_PYP
                  ),
                },
              },
              ...refetchQueries,
            ]
          : refetchQueries,
        awaitRefetchQueries: shouldRefetchQuery,
      });
      dispatch(
        setToastMsg({
          msg: toastMessage,
          locale_params: localeParams,
          type: "success",
          position: "toast-bottom-left",
        })
      );
    } catch (err) {
      dispatch(
        setToastMsg({
          msg: "toastMsgs:something_went_wrong",
          type: "warning",
          position: "toast-bottom-left",
        })
      );
    }
  };
};

export const updateVerticalOverviewFilters = data => {
  return { type: UPDATE_VERTICAL_OVERVIEW_FILTERS, data: data };
};

export const updateHorizontalOverviewFilters = data => {
  return { type: UPDATE_HORIZONTAL_OVERVIEW_FILTERS, data: data };
};

export const updateCurriculumMapsFilters = data => {
  return { type: UPDATE_CURRICULUM_MAPS_FILTERS, data: data };
};

export const updateCurrentGrade = data => {
  return { type: UPDATE_CURRENT_GRADE, data };
};

export const updateAdminInsightsSidebarSelectedItem = data => {
  return { type: UPDATE_ADMIN_INSIGHTS_SIDEBAR_SELECTED_ITEM, data };
};

export const updateConfigureUnitsFilters = data => {
  return { type: UPDATE_CONFIGURE_UNITS_FILTERS_OBJECT, data };
};

export const updateExcludeUnit = data => {
  return { type: UPDATE_EXCLUDE_UNIT_MODAL, data };
};

export const updateInsightUnitsStatus = data => {
  return { type: UPDATE_INSIGHT_UNIT_COUNTS, data };
};

export const updateCurrentCurriculumProgram = data => {
  return { type: UPDATE_CURRENT_CURRICULUM_PROGRAM, data };
};

export const resetCurriculumMapFilters = () => {
  return (dispatch, getState) => {
    const state = getState();
    const curriculumProgramId = _.get(
      state,
      "platform.currentCurriculumProgram.id",
      null
    );

    dispatch(
      updateCurriculumMapsFilters({
        ...INITIAL_CURRICULUM_MAP_FILTERS,
        curriculumProgramId,
      })
    );
  };
};

const updateFiltersInRoutes = ({ params = {} }) => {
  return dispatch => {
    let route = "";
    _.forEach(_.keys(params), key => {
      if (_.isArray(params[key]) || !_.isEmpty(_.trim(params[key]))) {
        route = route + `&${key}=${params[key]}`;
      }
    });

    dispatch(
      goToRelativeRoute({
        route: `?${route}`,
      })
    );
  };
};

export const updateFiltersInRoutesMemoize = _.memoize(
  params => updateFiltersInRoutes(params),
  params => JSON.stringify(params)
);

const REDUCER_HANDLERS = {
  [CHANGE_CURRENT_TAB]: (state, action) => {
    return update(state, { currentTab: { $set: action.data } });
  },
  [CHANGE_CURRENT_LEVEL]: (state, action) => {
    return update(state, { currentLevel: { $set: action.data } });
  },
  [UPDATE_CURRENT_GRADE]: (state, action) => {
    return update(state, { currentGrade: { $set: action.data } });
  },
  [UPDATE_ADMIN_INSIGHTS_SIDEBAR_SELECTED_ITEM]: (state, action) => {
    return update(state, {
      adminInsights: {
        selectedSidebarItem: {
          $set: action.data,
        },
      },
    });
  },
  [UPDATE_FILTERS_OBJECT]: (state, action) => {
    const params = action.data;

    _.map(Object.keys(params), key => {
      state = update(state, {
        filters: { [key]: { $set: params[key] } },
      });
    });

    return state;
  },
  [UPDATE_CONFIGURE_UNITS_FILTERS_OBJECT]: (state, action) => {
    const params = action.data;

    _.map(Object.keys(params), key => {
      state = update(state, {
        configureUnitsFilters: { [key]: { $set: params[key] } },
      });
    });

    return state;
  },
  [UPDATE_EXCLUDE_UNIT_MODAL]: (state, action) => {
    const params = action.data;

    _.map(Object.keys(params), key => {
      state = update(state, {
        excludeUnit: { [key]: { $set: params[key] } },
      });
    });

    return state;
  },
  [UPDATE_INSIGHT_UNIT_COUNTS]: (state, action) => {
    const params = action.data;

    _.map(Object.keys(params), key => {
      state = update(state, {
        insightUnitsStatus: { [key]: { $set: params[key] } },
      });
    });

    return state;
  },
  [UPDATE_HORIZONTAL_OVERVIEW_FILTERS]: (state, action) => {
    const params = action.data;

    _.map(Object.keys(params), key => {
      state = update(state, {
        horizontalOverview: { [key]: { $set: params[key] } },
      });
    });

    return state;
  },
  [UPDATE_CURRICULUM_MAPS_FILTERS]: (state, action) => {
    const params = action.data;

    _.map(Object.keys(params), key => {
      state = update(state, {
        curriculumMaps: { [key]: { $set: params[key] } },
      });
    });

    return state;
  },

  [UPDATE_VERTICAL_OVERVIEW_FILTERS]: (state, action) => {
    const params = action.data;

    _.map(Object.keys(params), key => {
      state = update(state, {
        verticalOverview: { [key]: { $set: params[key] } },
      });
    });

    return state;
  },
  [UPDATE_CURRENT_CURRICULUM_PROGRAM]: (state, action) => {
    return update(state, {
      currentCurriculumProgram: { $set: action.data },
    });
  },
};

const initialState = {
  currentTab: "units",
  currentLevel: null,
  isLoading: false,
  filters: {
    currentPlanningInsight: null,
  },
  curriculumMaps: INITIAL_CURRICULUM_MAP_FILTERS,
  horizontalOverview: {
    selectedSubjectIds: [],
    selectedUnitType: ["ALL"],
    levels: ["ALL"],
  },
  verticalOverview: {
    selectedGradeId: null,
    currentTableType: "",
    columnOptions: [],
    selectedSubjectIds: [],
    selectedUnitType: "ALL",
    selectedGrades: [],
    selectedStandardsGrades: [],
    selectedStandardsSubject: null,
  },
  adminInsights: {
    selectedSidebarItem: null,
  },
  configureUnitsFilters: {
    searchText: "",
    selectedGrades: null,
    selectedSubjects: null,
    selectedStatus: "ALL",
    selectedUnitType: "ALL",
    selectedTheme: "ALL",
    selectedTab: "included_units",
  },
  excludeUnit: {
    modalOpen: false,
    unitPlan: null,
    grade: null,
    isExcludingFromGrade: false,
  },
  insightUnitsStatus: {
    totalUnitsCount: 0,
    includedUnitsCount: 0,
    includedTransdisciplinaryUnitsCount: 0,
    noUnitsCreated: false,
    noUnitsIncluded: false,
    noTransdisciplinaryUnitsIncluded: false,
  },
  currentCurriculumProgram: {},
};

export default function myReducer(state = initialState, action) {
  const handler = REDUCER_HANDLERS[action.type];
  return handler ? handler(state, action) : state;
}
